import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
} from "react";
import { initializeApp, getApp, deleteApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  limit,
} from "firebase/firestore";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);
import "./App.css";

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const BACKEND_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" 
    ? "http://localhost:8001" 
    : "");

// Helper to safely format dates, handling different date formats
const formatPaymentDate = (dateVal) => {
  if (!dateVal) return "N/A";
  
  if (dateVal instanceof Date) {
    return dateVal.toLocaleDateString();
  }
  
  if (typeof dateVal === 'object' && dateVal.seconds !== undefined) {
    return new Date(dateVal.seconds * 1000).toLocaleDateString();
  }
  
  if (typeof dateVal !== 'string') {
    return "N/A";
  }

  try {
    if (dateVal.includes("T") || dateVal.includes(" ")) {
      const d = new Date(dateVal);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString();
      }
    }
    
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
      const d = new Date(dateVal + "T00:00:00");
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString();
      }
    }
    
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
  } catch (error) {
    console.error("Error formatting date:", dateVal, error);
    return "N/A";
  }
};

// Auth Context
const AuthContext = createContext(null);

// Theme Context
const ThemeContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Load saved theme preference
    const savedTheme = localStorage.getItem("ghmbl-theme");
    if (savedTheme === "dark") {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);

    if (newTheme) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("ghmbl-theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("ghmbl-theme", "light");
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const withId = (document) => ({ id: document.id, ...document.data() });

const getPlayers = async (onlyActive = false) => {
  const playersRef = collection(db, "players");
  const playersQuery = onlyActive
    ? query(playersRef, where("active", "==", true))
    : playersRef;
  const snapshot = await getDocs(playersQuery);
  return snapshot.docs.map(withId);
};

const getAttendance = async () => {
  const snapshot = await getDocs(
    query(collection(db, "attendance"), orderBy("date", "desc"))
  );
  return snapshot.docs.map(withId);
};

const getPayments = async (type = "all") => {
  const user = auth.currentUser;
  const paymentsRef = collection(db, "payments");
  let paymentsQuery = paymentsRef;

  if (type === "personal" && user?.uid) {
    paymentsQuery = query(paymentsRef, where("player_id", "==", user.uid));
  } else if (type === "ghmbl") {
    paymentsQuery = query(paymentsRef, where("player_id", "==", "ghmbl"));
  }

  const snapshot = await getDocs(paymentsQuery);
  return snapshot.docs.map(withId);
};

const getDashboardSummary = async (personal = false) => {
  const [sessions, payments, players] = await Promise.all([
    getAttendance(),
    getPayments(personal ? "personal" : "all"),
    getPlayers(true),
  ]);
  const currentUserId = auth.currentUser?.uid;
  const filteredSessions = personal
    ? sessions.filter((session) =>
        Array.isArray(session.player_ids) &&
        session.player_ids.includes(currentUserId)
      )
    : sessions;

  const totalRevenue = payments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0
  );
  const courtSessions = filteredSessions.length;
  const activePlayers = players.length;

  return { totalRevenue, courtSessions, activePlayers };
};

const handleFetchResponseError = async (response, fallbackMsg, targetUrl) => {
  if (response.ok) return;
  
  let errMsg = `${fallbackMsg}: Server returned status ${response.status} for URL ${targetUrl} (origin: ${window.location.origin})`;
  if (response.statusText) {
    errMsg += ` (${response.statusText})`;
  }
  
  try {
    const errText = await response.text();
    if (errText) {
      try {
        const errJson = JSON.parse(errText);
        const detail = errJson.detail || errJson.message;
        if (detail) {
          errMsg = typeof detail === "string" ? detail : JSON.stringify(detail);
        } else {
          errMsg = errText;
        }
      } catch {
        errMsg = errText;
      }
    }
  } catch (e) {
    console.error("Failed to read error response body", e);
  }
  
  console.error(`API Error: ${errMsg} (URL: ${targetUrl})`);
  throw new Error(errMsg);
};

const api = {
  get: async (path) => {
    if (path === "/api/players") return { data: await getPlayers(false) };
    if (path === "/api/players/active") return { data: await getPlayers(true) };
    if (path.startsWith("/api/players/")) {
      const playerId = path.replace("/api/players/", "");
      const snap = await getDoc(doc(db, "players", playerId));
      if (!snap.exists()) throw new Error("Player not found");
      return { data: withId(snap) };
    }
    if (path === "/api/attendance") return { data: await getAttendance() };
    if (path === "/api/payments") return { data: await getPayments("all") };
    if (path === "/api/payments/personal")
      return { data: await getPayments("personal") };
    if (path === "/api/payments/ghmbl")
      return { data: await getPayments("ghmbl") };
    if (path === "/api/dashboard") return { data: await getDashboardSummary() };
    if (path === "/api/dashboard/personal")
      return { data: await getDashboardSummary(true) };
    if (path === "/api/notifications") {
      const userId = auth.currentUser?.uid;
      const notificationsQuery = query(
        collection(db, "notifications"),
        where("player_id", "==", userId),
        orderBy("created_at", "desc"),
        limit(100)
      );
      const snapshot = await getDocs(notificationsQuery);
      return { data: snapshot.docs.map(withId) };
    }
    if (path === "/api/system/ghmbl-balance") {
      const snap = await getDoc(doc(db, "system", "ghmbl_balance"));
      return { data: { balance: snap.exists() ? snap.data().balance || 0 : 0 } };
    }
    if (path === "/api/database/backups") return { data: [] };
    throw new Error(`Unsupported Firebase GET path: ${path}`);
  },
  post: async (path, payload) => {
    if (path === "/api/players") {
      let tempApp;
      try {
        // Initialize a temporary Firebase app to create the user without signing out the current admin
        try {
          tempApp = getApp("TempApp");
        } catch {
          tempApp = initializeApp(firebaseConfig, "TempApp");
        }
        const tempAuth = getAuth(tempApp);
        
        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(
          tempAuth,
          payload.email,
          payload.password
        );
        const newUid = userCredential.user.uid;
        
        // Sign out of the temporary auth instance
        await signOut(tempAuth);
        
        // Prepare data for Firestore, omitting the password field
        const { password, ...firestoreData } = payload;
        const playerData = {
          ...firestoreData,
          id: newUid,
          balance: firestoreData.balance !== undefined ? Number(firestoreData.balance) : 0.0,
          active: firestoreData.active !== false,
          join_date: firestoreData.join_date || new Date().toISOString(),
        };
        
        // Save to players collection in Firestore using the UID
        await setDoc(doc(db, "players", newUid), playerData);
        return { data: playerData };
      } finally {
        if (tempApp) {
          try {
            await deleteApp(tempApp);
          } catch (deleteError) {
            console.error("Error deleting temporary app:", deleteError);
          }
        }
      }
    }
    if (path === "/api/attendance") {
      // Calculate court cost dynamically based on hourly rates
      const getCourtCost = (courtType, hours) => {
        const hourlyRate = String(courtType).toLowerCase() === "wood" ? 6.82 : 9.74;
        return Number((hours * hourlyRate).toFixed(2));
      };

      const sessionCost = getCourtCost(payload.court_type, payload.hours);
      const totalPlayers = (payload.players_present?.length || 0) + (payload.guests_present?.length || 0);
      const costPerPerson = totalPlayers > 0 ? Number((sessionCost / totalPlayers).toFixed(2)) : 0.0;

      const sessionData = {
        ...payload,
        total_cost: sessionCost,
        cost_per_person: costPerPerson,
        created_at: payload.created_at || new Date().toISOString(),
      };

      const created = await addDoc(collection(db, "attendance"), sessionData);

      // Deduct cost from player balances in Firestore
      if (costPerPerson > 0) {
        if (Array.isArray(payload.players_present)) {
          for (const playerId of payload.players_present) {
            const playerRef = doc(db, "players", playerId);
            const playerSnap = await getDoc(playerRef);
            if (playerSnap.exists()) {
              const currentBalance = Number(playerSnap.data().balance || 0);
              await updateDoc(playerRef, {
                balance: Number((currentBalance - costPerPerson).toFixed(2))
              });
            }
          }
        }
        if (Array.isArray(payload.guests_present)) {
          for (const guestId of payload.guests_present) {
            const guestRef = doc(db, "guests", guestId);
            const guestSnap = await getDoc(guestRef);
            if (guestSnap.exists()) {
              const currentBalance = Number(guestSnap.data().balance || 0);
              await updateDoc(guestRef, {
                balance: Number((currentBalance - costPerPerson).toFixed(2))
              });
            }
          }
        }
      }

      // Update system ghmbl_balance (deduct session cost)
      if (sessionCost > 0) {
        const systemRef = doc(db, "system", "ghmbl_balance");
        const systemSnap = await getDoc(systemRef);
        const currentBalance = systemSnap.exists() ? Number(systemSnap.data().balance || 0) : 0;
        await setDoc(systemRef, {
          balance: Number((currentBalance - sessionCost).toFixed(2))
        }, { merge: true });
      }

      return { data: { ...sessionData, id: created.id } };
    }
    if (path === "/api/payments") {
      const created = await addDoc(collection(db, "payments"), payload);
      
      // Update player balance in Firebase-only mode
      if (payload.player_id && payload.player_id !== "ghmbl") {
        const playerRef = doc(db, "players", payload.player_id);
        const playerSnap = await getDoc(playerRef);
        if (playerSnap.exists()) {
          const currentBalance = Number(playerSnap.data().balance || 0);
          const paymentAmount = Number(payload.amount || 0);
          await updateDoc(playerRef, {
            balance: Number((currentBalance + paymentAmount).toFixed(2))
          });
        }
      } else if (payload.player_id === "ghmbl") {
        const systemRef = doc(db, "system", "ghmbl_balance");
        const systemSnap = await getDoc(systemRef);
        const currentBalance = systemSnap.exists() ? Number(systemSnap.data().balance || 0) : 0;
        const paymentAmount = Number(payload.amount || 0);
        await setDoc(systemRef, {
          balance: Number((currentBalance + paymentAmount).toFixed(2))
        }, { merge: true });
      }
      
      return { data: { ...payload, id: created.id } };
    }
    if (path.startsWith("/api/players/") && path.endsWith("/send-reminder")) {
      const token = await auth.currentUser?.getIdToken();
      const targetUrl = `${BACKEND_URL}${path}`;
      const response = await fetch(targetUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });
      await handleFetchResponseError(response, "Failed to send payment reminder", targetUrl);
      const data = await response.json();
      return { data };
    }
    if (path === "/api/system/send-balance-reminders") {
      const token = await auth.currentUser?.getIdToken();
      const targetUrl = `${BACKEND_URL}${path}`;
      const response = await fetch(targetUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      await handleFetchResponseError(response, "Failed to send balance reminders", targetUrl);
      const data = await response.json();
      return { data };
    }
    if (path === "/api/database/backup" || path === "/api/database/restore") {
      return { data: { success: false, message: "Not supported on Firebase-only mode" } };
    }
    throw new Error(`Unsupported Firebase POST path: ${path}`);
  },
  put: async (path, payload) => {
    if (path.startsWith("/api/notifications/") && path.endsWith("/read")) {
      const notificationId = path.split("/")[3];
      await updateDoc(doc(db, "notifications", notificationId), { is_read: true });
      return { data: { success: true } };
    }
    if (path.startsWith("/api/players/") && path.endsWith("/status")) {
      const playerId = path.split("/")[3];
      await updateDoc(doc(db, "players", playerId), payload);
      return { data: { success: true } };
    }
    if (path.startsWith("/api/players/")) {
      const playerId = path.split("/")[3];
      await updateDoc(doc(db, "players", playerId), payload);
      return { data: { success: true } };
    }
    if (path.startsWith("/api/payments/")) {
      const paymentId = path.split("/")[3];
      
      // Adjust balance by difference before updating payment doc
      const paymentRef = doc(db, "payments", paymentId);
      const paymentSnap = await getDoc(paymentRef);
      if (paymentSnap.exists()) {
        const oldPayment = paymentSnap.data();
        const oldAmount = Number(oldPayment.amount || 0);
        const newAmount = Number(payload.amount || 0);
        const difference = newAmount - oldAmount;
        
        if (difference !== 0) {
          if (payload.player_id && payload.player_id !== "ghmbl") {
            const playerRef = doc(db, "players", payload.player_id);
            const playerSnap = await getDoc(playerRef);
            if (playerSnap.exists()) {
              const currentBalance = Number(playerSnap.data().balance || 0);
              await updateDoc(playerRef, {
                balance: Number((currentBalance + difference).toFixed(2))
              });
            }
          } else if (payload.player_id === "ghmbl") {
            const systemRef = doc(db, "system", "ghmbl_balance");
            const systemSnap = await getDoc(systemRef);
            if (systemSnap.exists()) {
              const currentBalance = Number(systemSnap.data().balance || 0);
              await setDoc(systemRef, {
                balance: Number((currentBalance + difference).toFixed(2))
              }, { merge: true });
            }
          }
        }
      }

      await updateDoc(doc(db, "payments", paymentId), payload);
      return { data: { success: true } };
    }
    if (path === "/api/system/ghmbl-balance") {
      await setDoc(doc(db, "system", "ghmbl_balance"), payload || {}, {
        merge: true,
      });
      return { data: { success: true } };
    }
    throw new Error(`Unsupported Firebase PUT path: ${path}`);
  },
  delete: async (path) => {
    if (path.startsWith("/api/players/")) {
      const playerId = path.split("/")[3];
      await deleteDoc(doc(db, "players", playerId));
      return { data: { success: true } };
    }
    if (path.startsWith("/api/attendance/")) {
      const sessionId = path.split("/")[3];
      
      // Get the session document to adjust balances before deleting
      const sessionRef = doc(db, "attendance", sessionId);
      const sessionSnap = await getDoc(sessionRef);
      if (sessionSnap.exists()) {
        const sessionData = sessionSnap.data();
        const costPerPerson = Number(sessionData.cost_per_person || 0);
        const sessionCost = Number(sessionData.total_cost || 0);
        
        if (costPerPerson > 0) {
          if (Array.isArray(sessionData.players_present)) {
            for (const playerId of sessionData.players_present) {
              const playerRef = doc(db, "players", playerId);
              const playerSnap = await getDoc(playerRef);
              if (playerSnap.exists()) {
                const currentBalance = Number(playerSnap.data().balance || 0);
                await updateDoc(playerRef, {
                  balance: Number((currentBalance + costPerPerson).toFixed(2))
                });
              }
            }
          }
          if (Array.isArray(sessionData.guests_present)) {
            for (const guestId of sessionData.guests_present) {
              const guestRef = doc(db, "guests", guestId);
              const guestSnap = await getDoc(guestRef);
              if (guestSnap.exists()) {
                const currentBalance = Number(guestSnap.data().balance || 0);
                await updateDoc(guestRef, {
                  balance: Number((currentBalance + costPerPerson).toFixed(2))
                });
              }
            }
          }
        }
        
        if (sessionCost > 0) {
          const systemRef = doc(db, "system", "ghmbl_balance");
          const systemSnap = await getDoc(systemRef);
          if (systemSnap.exists()) {
            const currentBalance = Number(systemSnap.data().balance || 0);
            await setDoc(systemRef, {
              balance: Number((currentBalance + sessionCost).toFixed(2))
            }, { merge: true });
          }
        }
      }

      await deleteDoc(doc(db, "attendance", sessionId));
      return { data: { success: true } };
    }
    if (path.startsWith("/api/payments/")) {
      const paymentId = path.split("/")[3];
      
      // Adjust balance by difference (deducting the payment) before deleting doc
      const paymentRef = doc(db, "payments", paymentId);
      const paymentSnap = await getDoc(paymentRef);
      if (paymentSnap.exists()) {
        const paymentData = paymentSnap.data();
        const paymentAmount = Number(paymentData.amount || 0);
        
        if (paymentData.player_id && paymentData.player_id !== "ghmbl") {
          const playerRef = doc(db, "players", paymentData.player_id);
          const playerSnap = await getDoc(playerRef);
          if (playerSnap.exists()) {
            const currentBalance = Number(playerSnap.data().balance || 0);
            await updateDoc(playerRef, {
              balance: Number((currentBalance - paymentAmount).toFixed(2))
            });
          }
        } else if (paymentData.player_id === "ghmbl") {
          const systemRef = doc(db, "system", "ghmbl_balance");
          const systemSnap = await getDoc(systemRef);
          if (systemSnap.exists()) {
            const currentBalance = Number(systemSnap.data().balance || 0);
            await setDoc(systemRef, {
              balance: Number((currentBalance - paymentAmount).toFixed(2))
            }, { merge: true });
          }
        }
      }

      await deleteDoc(doc(db, "payments", paymentId));
      return { data: { success: true } };
    }
    throw new Error(`Unsupported Firebase DELETE path: ${path}`);
  },
};

// Utility functions
const getUserRole = (user) => {
  if (!user?.email) return "player";
  if (user.email === "eltouch@gmail.com") return "owner";
  // For other users, we'd need to check the database, but for simplicity
  // we'll default to 'player' since most components use local state
  return "player";
};

const isOwnerOrAdmin = (user) => {
  const role = getUserRole(user);
  return role === "owner" || role === "admin";
};

// Format date to Central Time Zone (US)
const formatDateCentral = (dateString) => {
  if (!dateString) return "N/A";

  // Create date object from the string
  // If it's a date-only string (YYYY-MM-DD), treat it as local date, not UTC
  const date = new Date(dateString + "T12:00:00"); // Add noon time to avoid timezone shifts

  // Format using Central Time Zone
  return date.toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

// Components

// Login Screen
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      onLogin();
    } catch (error) {
      // Show user-friendly error messages
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        setError("Password is incorrect. Please try again.");
      } else if (error.code === "auth/user-not-found") {
        setError("No account found with this email address.");
      } else if (error.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many failed login attempts. Please try again later.");
      } else {
        setError("Login failed. Please check your credentials and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-600 dark:bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">🏸</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            GHMBL
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Greater Houston Malayalee Badminton League
          </p>
        </div>

        <form onSubmit={handleAuth} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id="showPasswordLogin"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label
                htmlFor="showPasswordLogin"
                className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >
                Show password
              </label>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Notifications Component
const NotificationsDropdown = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      fetchNotifications();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/notifications");
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/api/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M15 17h5l-5 5v-5zM4 19h10a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Notifications
            </h3>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                    !notification.read ? "bg-blue-50 dark:bg-blue-900/20" : ""
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start space-x-2">
                    <div
                      className={`w-2 h-2 rounded-full mt-2 ${
                        notification.read ? "bg-gray-300" : "bg-blue-500"
                      }`}
                    ></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = ({ currentScreen, setCurrentScreen, onLogout }) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState("player");
  const [userType, setUserType] = useState("regular");

  useEffect(() => {
    const determineNavRole = async () => {
      if (!user) return;

      // Check if user is the owner
      if (user?.email === "eltouch@gmail.com") {
        console.log("Navigation: Setting role to owner for eltouch@gmail.com");
        setUserRole("owner");
        setUserType("regular");
        return;
      }

      // For other users, fetch their role and user_type from the players collection
      try {
        const response = await api.get(`/api/players/${user.uid}`);
        const playerData = response.data;

        if (playerData) {
          // Set user role (admin, player)
          if (playerData.role) {
            console.log(
              `Navigation: Setting role to ${playerData.role} for ${user.email}`
            );
            setUserRole(playerData.role);
          } else {
            console.log(
              `Navigation: Setting default role 'player' for ${user.email}`
            );
            setUserRole("player");
          }

          // Set user type (guest, regular)
          if (playerData.user_type) {
            console.log(
              `Navigation: Setting user_type to ${playerData.user_type} for ${user.email}`
            );
            setUserType(playerData.user_type);
          } else {
            setUserType("regular");
          }
        } else {
          console.log(
            `Navigation: Setting default role 'player' for ${user.email}`
          );
          setUserRole("player");
          setUserType("regular");
        }
      } catch (error) {
        // User might not have a player record yet - this is okay, they default to 'player' role
        if (error.response?.status !== 404) {
          console.error(
            "Navigation: Error fetching user role from database:",
            error
          );
        }
        console.log(
          `Navigation: Setting fallback role 'player' for ${user.email}`
        );
        setUserRole("player");
        setUserType("regular");
      }
    };

    determineNavRole();
  }, [user]);

  const navItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: "📊",
      roles: ["owner", "admin", "player", "guest"],
    },
    {
      id: "players",
      label: "Players",
      icon: "👥",
      roles: ["owner", "admin", "player", "guest"],
    }, // Guests can only view their own profile
    {
      id: "attendance",
      label: "Attendance",
      icon: "📅",
      roles: ["owner", "admin", "player", "guest"],
    }, // Guests can view their own
    {
      id: "payments",
      label: "Payments",
      icon: "💰",
      roles: ["owner", "admin", "player", "guest"],
    }, // Guests can view their own
    {
      id: "reports",
      label: "Reports",
      icon: "📈",
      roles: ["owner", "admin", "player", "guest"],
    }, // Guests can view their own
    {
      id: "mindbody",
      label: "HBC Charges",
      icon: "🔗",
      roles: ["owner", "admin", "player"],
    }, // Guests cannot access
    {
      id: "account",
      label: "Settings",
      icon: "⚙️",
      roles: ["owner", "admin", "player", "guest"],
    }, // All can manage their profile
  ];

  // Filter nav items based on user role and user type
  const filteredNavItems = navItems.filter((item) => {
    // Check if user's role is allowed for this item
    const roleAllowed = item.roles.includes(userRole);

    // Additionally, hide HBC Charges from guests
    if (item.id === "mindbody" && userType === "guest") {
      return false;
    }

    return roleAllowed;
  });

  const handleNavItemClick = (screenId) => {
    setCurrentScreen(screenId);
    setIsMobileMenuOpen(false); // Close mobile menu when item is clicked
  };

  const handleLogout = () => {
    onLogout();
    setIsMobileMenuOpen(false); // Close mobile menu when logging out
  };

  // All users (including players) now see the navigation menu with filtered items

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
      >
        <svg
          className="w-6 h-6 text-gray-700 dark:text-gray-300"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M4 6h16M4 12h16M4 18h16"></path>
        </svg>
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed lg:relative inset-y-0 left-0 z-50 w-64 
        bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 
        transform transition-transform duration-300 ease-in-out
        ${
          isMobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0"
        }
      `}
      >
        <div className="p-6">
          {/* Mobile Close Button */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-600 dark:bg-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">🏸</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  GHMBL
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Court Manager
                </p>
              </div>
            </div>

            {/* Close button for mobile */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* User Role Indicator */}
          <div className="mb-6 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center space-x-2">
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  userRole === "owner"
                    ? "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300"
                    : userRole === "admin"
                    ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-300"
                    : userRole === "guest"
                    ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                    : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                }`}
              >
                {userRole === "owner" && "👑 Owner"}
                {userRole === "admin" && "⚡ Admin"}
                {userRole === "player" && "🏸 Player"}
                {userRole === "guest" && "👤 Guest"}
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {userRole === "owner"
                ? "Full system access"
                : userRole === "admin"
                ? "Admin privileges"
                : userRole === "guest"
                ? "Guest access"
                : "Player access"}
            </p>
          </div>

          <nav className="space-y-2">
            {filteredNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavItemClick(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  currentScreen === item.id
                    ? "bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-r-2 border-indigo-600 dark:border-indigo-400"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            >
              <span className="text-lg">🚪</span>
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Reusable Accordion Component
const Accordion = ({ title, children, defaultOpen = false, icon = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center space-x-3">
          {icon && <span className="text-xl">{icon}</span>}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

// Dashboard Screen
const DashboardScreen = ({ setCurrentScreen }) => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("ghmbl"); // 'ghmbl' or 'personal'
  const [userRole, setUserRole] = useState("player");
  const [userType, setUserType] = useState(""); // Track user_type (regular_player, guest, other)
  const [userName, setUserName] = useState(""); // Store user's actual name
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [isSelectingForCancel, setIsSelectingForCancel] = useState(false);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [selectedSessionPlayers, setSelectedSessionPlayers] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("thisMonth");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [filteredRecentSessions, setFilteredRecentSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]); // For calendar display
  const [selectedDateSessions, setSelectedDateSessions] = useState(null); // Sessions for clicked date
  const [showDateModal, setShowDateModal] = useState(false); // Modal to show players

  useEffect(() => {
    if (user) {
      determineUserRole(); // Now async
    }
  }, [user]);

  useEffect(() => {
    if (user && userRole) {
      fetchDashboardData();
      fetchPlayers();
      fetchAllSessions();
    }
  }, [user, userRole, viewMode]);

  useEffect(() => {
    if (dashboardData?.recent_sessions) {
      applyTimePeriodFilter();
    }
  }, [dashboardData, selectedTimePeriod, customStartDate, customEndDate]);

  const determineUserRole = async () => {
    if (!user?.uid) return;

    try {
      // Check if user is the owner
      if (user?.email === "eltouch@gmail.com") {
        console.log("Setting role to owner for eltouch@gmail.com");
        setUserRole("owner");
        setUserName("Ben"); // Owner's name
        setViewMode("personal"); // Default to personal view for owner
        return;
      }

      // For other users, fetch their role from the players collection
      const response = await api.get(`/api/players/${user.uid}`);
      const playerData = response.data;

      if (playerData) {
        // Store user_type (regular_player, guest, other)
        if (playerData.user_type) {
          setUserType(playerData.user_type);
        } else {
          setUserType("regular_player"); // Default to regular_player
        }

        if (playerData.role) {
          console.log(`Setting role to ${playerData.role} for ${user.email}`);
          setUserRole(playerData.role); // Could be 'admin' or 'player'
        } else {
          console.log(`Setting default role 'player' for ${user.email}`);
          setUserRole("player"); // Default to player if no role specified
        }

        // Store the user's actual name
        if (playerData.name) {
          setUserName(playerData.name);
        }
      } else {
        setUserRole("player");
      }

      // Force personal view for all non-owner users
      setViewMode("personal");
    } catch (error) {
      console.error("Error determining user role:", error);
      // Fallback to player role if there's an error
      console.log(`Setting fallback role 'player' for ${user.email}`);
      setUserRole("player");
      setViewMode("personal");
    }
  };

  const fetchDashboardData = async () => {
    if (!user) return;

    try {
      let endpoint = "/api/dashboard";

      // For regular users (players and guests), always get personal data
      // For owner and admin, get personal data if viewMode is 'personal', otherwise get full dashboard
      if (
        userRole === "player" ||
        userRole === "guest" ||
        ((userRole === "owner" || userRole === "admin") &&
          viewMode === "personal")
      ) {
        endpoint = `/api/dashboard/personal`;
      }

      console.log(`Fetching dashboard data for ${user.email} from ${endpoint}`);

      const response = await api.get(endpoint);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // For now, set empty data to prevent crashes
      setDashboardData({
        total_hours: 0,
        total_cost: 0,
        total_sessions: 0,
        active_players: userRole === "player" ? 1 : 0,
        recent_sessions: [],
        missed_cancellations: [], // Days when cancellation was forgotten
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await api.get("/api/players");
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const fetchAllSessions = async () => {
    try {
      const response = await api.get("/api/attendance");
      let sessions = response.data || [];

      // Filter based on view mode
      if (viewMode === "personal" && user?.uid) {
        // Personal view: only show sessions user attended
        sessions = sessions.filter(
          (session) =>
            session.players_present &&
            session.players_present.includes(user.uid)
        );
      }

      setAllSessions(sessions);
    } catch (error) {
      console.error("Error fetching all sessions:", error);
    }
  };

  const handleShowPlayers = (session, event) => {
    event.stopPropagation();
    setSelectedSessionPlayers(session);
    setShowPlayersModal(true);
  };

  const getPlayerName = (playerId) => {
    const player = players.find((p) => p.id === playerId);
    return player ? player.name : "Unknown Player";
  };

  const getDateRange = (filter) => {
    const now = new Date();
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    );

    switch (filter) {
      case "thisMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
        };
      case "lastMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
        };
      case "thisWeek":
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return { start: startOfWeek, end: endOfDay };
      case "lastWeek":
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);
        return { start: lastWeekStart, end: lastWeekEnd };
      case "last6Months":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 6, 1),
          end: endOfDay,
        };
      case "thisYear":
        return { start: new Date(now.getFullYear(), 0, 1), end: endOfDay };
      case "lastYear":
        return {
          start: new Date(now.getFullYear() - 1, 0, 1),
          end: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59),
        };
      case "custom":
        return {
          start: customStartDate
            ? new Date(customStartDate)
            : new Date(2020, 0, 1),
          end: customEndDate ? new Date(customEndDate + "T23:59:59") : endOfDay,
        };
      default:
        return { start: new Date(2020, 0, 1), end: endOfDay };
    }
  };

  const applyTimePeriodFilter = () => {
    if (!dashboardData?.recent_sessions) return;

    let filtered = [...dashboardData.recent_sessions];

    if (selectedTimePeriod !== "all") {
      const { start, end } = getDateRange(selectedTimePeriod);
      filtered = filtered.filter((session) => {
        // Parse date without timezone conversion
        const sessionDate = new Date(session.date + "T12:00:00");
        return sessionDate >= start && sessionDate <= end;
      });
    }

    setFilteredRecentSessions(filtered);
  };

  // Navigation handlers for interactive cards
  const handleCardClick = (cardType) => {
    if (setCurrentScreen) {
      setCurrentScreen(cardType);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getSessionDays = () => {
    if (!allSessions || allSessions.length === 0)
      return { held: new Set(), canceled: new Set(), sessionsByDate: {} };

    const heldDays = new Set();
    const canceledDays = new Set();
    const sessionsByDate = {};

    allSessions.forEach((session) => {
      // Parse date without timezone conversion by treating as local date
      const sessionDate = new Date(session.date + "T12:00:00");
      if (
        sessionDate.getMonth() === currentDate.getMonth() &&
        sessionDate.getFullYear() === currentDate.getFullYear()
      ) {
        const day = sessionDate.getDate();

        // Store session by date for click handling
        if (!sessionsByDate[day]) {
          sessionsByDate[day] = [];
        }
        sessionsByDate[day].push(session);

        // Categorize as held or canceled
        if (session.canceled || session.status === "canceled") {
          canceledDays.add(day);
        } else {
          heldDays.add(day);
        }
      }
    });

    return { held: heldDays, canceled: canceledDays, sessionsByDate };
  };

  const handleDateClick = (day, sessionData) => {
    if (sessionData && sessionData.length > 0) {
      setSelectedDateSessions(sessionData);
      setShowDateModal(true);
    }
  };

  const formatMonth = (date) => {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1)
    );
  };

  const canSwitchView = () => {
    // Owner, Admin, and regular_player (by user_type) can switch between GHMBL and Personal views
    // Guests and other user types cannot switch
    const result =
      userRole === "owner" ||
      userRole === "admin" ||
      userType === "regular_player";
    console.log(
      `canSwitchView check: email=${user?.email}, userRole=${userRole}, userType=${userType}, result=${result}`
    );
    return result;
  };

  const canManageAttendance = () => {
    // Both owner and admin can log attendance and cancel sessions
    return userRole === "owner" || userRole === "admin";
  };

  const getUserFirstName = () => {
    if (!user?.email) return "User";

    // Use the userName state if available (fetched from player data)
    if (userName) {
      return userName.split(" ")[0]; // Get first name
    }

    // Check if we have dashboard data with user name
    if (dashboardData?.user_name) {
      return dashboardData.user_name.split(" ")[0]; // Get first name
    }

    // For owner account
    if (user.email === "eltouch@gmail.com") {
      return "Ben";
    }

    // Fallback: Extract first name from email username (before @)
    // This capitalizes the first letter for better presentation
    const emailUsername = user.email.split("@")[0];
    return emailUsername.charAt(0).toUpperCase() + emailUsername.slice(1);
  };

  const getViewTitle = () => {
    const firstName = getUserFirstName();

    // For guests - always show personal dashboard
    if (userType === "guest" || userType === "other") {
      return `${firstName}'s Dashboard`;
    }

    // For regular players and admins - show based on view mode
    if (userRole === "player" || userRole === "admin") {
      if (viewMode === "ghmbl") {
        return "GHMBL Dashboard";
      }
      return `${firstName}'s Dashboard`;
    }

    // For owner, show personal dashboard by default (unless explicitly in org view)
    if (userRole === "owner") {
      if (viewMode === "organization") {
        return "GHMBL Dashboard";
      } else if (viewMode === "ghmbl") {
        return "GHMBL Dashboard";
      } else {
        return `${firstName}'s Dashboard`; // Default to personal view for owner
      }
    }

    return "GHMBL Dashboard";
  };

  const getViewSubtitle = () => {
    const firstName = getUserFirstName();

    // For guests and other types - always personal
    if (userType === "guest" || userType === "other") {
      return `${firstName}'s personal activity for ${formatMonth(currentDate)}`;
    }

    // For all users with access, show based on view mode
    if (viewMode === "personal") {
      return `${firstName}'s personal activity for ${formatMonth(currentDate)}`;
    }

    return `League overview for ${formatMonth(currentDate)}`;
  };

  const handleLogAttendance = () => {
    // Open the add session modal for logging attendance
    setShowAddSession(true);
    setIsSelectingForCancel(false); // Regular attendance logging
  };

  const handleCancelToday = () => {
    // Open the add session modal for canceling today's session
    setShowAddSession(true);
    setIsSelectingForCancel(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600 dark:text-gray-300">
          Loading dashboard...
        </div>
      </div>
    );
  }

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOfMonth = getFirstDayOfMonth(currentDate);
  const {
    held: heldSessionDays,
    canceled: canceledSessionDays,
    sessionsByDate,
  } = getSessionDays();
  const today = new Date();
  const isCurrentMonth =
    currentDate.getMonth() === today.getMonth() &&
    currentDate.getFullYear() === today.getFullYear();

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header with Filter Dropdown */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {getViewTitle()}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              {getViewSubtitle()}
            </p>
          </div>

          {/* Filter Dropdown for Owner/Admin */}
          {canSwitchView() && (
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <svg
                  className="w-5 h-5 text-gray-600 dark:text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {viewMode === "ghmbl" ? "GHMBL View" : "Personal View"}
                </span>
                <svg
                  className="w-4 h-4 text-gray-600 dark:text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setViewMode("ghmbl");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        viewMode === "ghmbl"
                          ? "bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">🏸</span>
                        <div>
                          <p className="font-medium">GHMBL View</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            View all players data
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setViewMode("personal");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        viewMode === "personal"
                          ? "bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">👤</span>
                        <div>
                          <p className="font-medium">My Personal View</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            View your personal data only
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Role Indicator */}
        <div className="flex items-center space-x-4">
          <span
            className={`px-3 py-1 text-sm font-semibold rounded-full ${
              userRole === "owner"
                ? "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300"
                : userRole === "admin"
                ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-300"
                : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
            }`}
          >
            {userRole === "owner" && "👑 Owner"}
            {userRole === "admin" && "⚡ Admin"}
            {userRole === "player" && "🏸 Player"}
          </span>

          {canSwitchView() && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Viewing:{" "}
              {viewMode === "ghmbl" ? "All Players Data" : "Personal Data Only"}
            </span>
          )}
        </div>
      </div>

      {/* Interactive Monthly Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Hours Card - Clickable to Reports */}
        <div
          onClick={() => handleCardClick("reports")}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {userRole === "player" || viewMode === "personal"
                  ? "My Hours"
                  : "Total Hours"}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData?.total_hours || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <span className="text-xl">⏰</span>
            </div>
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Click to view reports →
            </p>
          </div>
        </div>

        {/* Total Cost Card - Clickable to Payments */}
        <div
          onClick={() => handleCardClick("payments")}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {userRole === "player" || viewMode === "personal"
                  ? "My Cost"
                  : "Total Cost"}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${(dashboardData?.total_cost || 0).toFixed(2)}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <span className="text-xl">💵</span>
            </div>
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-xs text-green-600 dark:text-green-400">
              Click to view payments →
            </p>
          </div>
        </div>

        {/* Sessions Card - Clickable to Attendance */}
        <div
          onClick={() => handleCardClick("attendance")}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {userRole === "player" || viewMode === "personal"
                  ? "My Sessions"
                  : "Sessions"}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData?.total_sessions || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <span className="text-xl">🏸</span>
            </div>
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-xs text-purple-600 dark:text-purple-400">
              Click to view attendance →
            </p>
          </div>
        </div>

        {/* Players/Balance Card - Clickable to Players */}
        <div
          onClick={() => handleCardClick("players")}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {userRole === "player"
                  ? "My Balance"
                  : viewMode === "personal"
                  ? "My Balance"
                  : "Active Players"}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {userRole === "player" || viewMode === "personal"
                  ? `$${dashboardData?.my_balance || 0}`
                  : dashboardData?.active_players || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <span className="text-xl">
                {userRole === "player" || viewMode === "personal" ? "💰" : "👥"}
              </span>
            </div>
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-xs text-orange-600 dark:text-orange-400">
              {userRole === "player" || viewMode === "personal"
                ? "View account →"
                : "Click to view players →"}
            </p>
          </div>
        </div>
      </div>

      {/* Action Links for Owner/Admin */}
      {canManageAttendance() && (
        <div className="flex items-center space-x-6 mb-8">
          <button
            onClick={handleLogAttendance}
            className="flex items-center space-x-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors group"
          >
            <svg
              className="w-4 h-4 group-hover:scale-110 transition-transform"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
            </svg>
            <span className="text-sm font-medium">Log Attendance</span>
          </button>
          <button
            onClick={handleCancelToday}
            className="flex items-center space-x-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors group"
          >
            <svg
              className="w-4 h-4 group-hover:scale-110 transition-transform"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            <span className="text-sm font-medium">Cancel Today's</span>
          </button>
        </div>
      )}

      {/* Overall Booking Calendar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
        {/* Overall Booking Title - Outside of header */}
        <div className="p-6 pb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {userRole === "player" ||
            userRole === "guest" ||
            viewMode === "personal"
              ? "My Booking Calendar"
              : "Overall Booking"}
          </h2>
        </div>

        {/* Month Navigation Header */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center space-x-8">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white min-w-[250px] text-center">
              {formatMonth(currentDate)}
            </h3>
            <button
              onClick={() => navigateMonth(1)}
              className="p-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 pb-6">
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-4 mb-4">
            {/* Day Headers */}
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2 w-12 mx-auto"
              >
                {day}
              </div>
            ))}

            {/* Empty cells for days before month starts */}
            {Array.from({ length: firstDayOfMonth }, (_, i) => (
              <div key={`empty-${i}`} className="w-12 h-12 mx-auto"></div>
            ))}

            {/* Days of the month */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const hasHeldSession = heldSessionDays.has(day);
              const hasCanceledSession = canceledSessionDays.has(day);
              const isToday = isCurrentMonth && day === today.getDate();
              const daySessionData = sessionsByDate[day];

              return (
                <div
                  key={day}
                  onClick={() =>
                    hasHeldSession && handleDateClick(day, daySessionData)
                  }
                  className={`relative w-12 h-12 mx-auto flex items-center justify-center text-sm font-medium transition-all ${
                    hasHeldSession ? "cursor-pointer hover:scale-110" : ""
                  } ${
                    isToday && !hasHeldSession && !hasCanceledSession
                      ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-full"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  <span
                    className={`relative z-10 ${
                      hasHeldSession || hasCanceledSession ? "text-white" : ""
                    }`}
                  >
                    {day}
                  </span>

                  {/* Held session indicator circle (Green) - Clickable */}
                  {hasHeldSession && (
                    <div className="absolute inset-0 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
                      <span className="text-white font-semibold text-sm">
                        {day}
                      </span>
                    </div>
                  )}

                  {/* Canceled session indicator circle (Red) - Not clickable */}
                  {hasCanceledSession && !hasHeldSession && (
                    <div className="absolute inset-0 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {day}
                      </span>
                    </div>
                  )}

                  {/* Today indicator ring */}
                  {isToday && !hasHeldSession && !hasCanceledSession && (
                    <div className="absolute inset-0 border-2 border-indigo-500 rounded-full"></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center space-x-6 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {userRole === "player" || viewMode === "personal"
                  ? "My Session (Click to view)"
                  : "Sessions Held (Click to view)"}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Canceled Sessions
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-indigo-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Today
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {userRole === "player" || viewMode === "personal"
                ? "My Recent Sessions"
                : "Recent Sessions"}
            </h2>
            <div className="flex items-center space-x-4">
              <select
                value={selectedTimePeriod}
                onChange={(e) => {
                  setSelectedTimePeriod(e.target.value);
                  setShowCustomDate(e.target.value === "custom");
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              >
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="thisWeek">This Week</option>
                <option value="lastWeek">Last Week</option>
                <option value="last6Months">Last 6 Months</option>
                <option value="thisYear">This Year</option>
                <option value="lastYear">Last Year</option>
                <option value="custom">Custom Range</option>
                <option value="all">All Time</option>
              </select>
              {showCustomDate && (
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    placeholder="Start Date"
                  />
                  <span className="text-gray-500 dark:text-gray-400">to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    placeholder="End Date"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {filteredRecentSessions?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Date
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Court
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Hours
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Players
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Total Cost
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Cost/Person
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRecentSessions.slice(0, 10).map((session, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {formatDateCentral(session.date)}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          session.court_type === "Wood"
                            ? "bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300"
                            : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                        }`}
                      >
                        {session.court_type}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white">
                        {session.hours}h
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <button
                        onClick={(e) => handleShowPlayers(session, e)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium cursor-pointer underline"
                      >
                        {(session.players_present?.length || 0) +
                          (session.guests_present?.length || 0)}{" "}
                        {(session.players_present?.length || 0) +
                          (session.guests_present?.length || 0) ===
                        1
                          ? "player"
                          : "players"}
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        ${(session.total_cost || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-700 dark:text-gray-300">
                        ${(session.cost_per_person || 0).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6">
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <span className="text-6xl mb-4 block">🏸</span>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {userRole === "player" || viewMode === "personal"
                  ? "No sessions played yet"
                  : "No sessions recorded yet"}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {selectedTimePeriod === "all" || !selectedTimePeriod
                  ? userRole === "player"
                    ? "Join a badminton session to see it here"
                    : viewMode === "personal"
                    ? "You haven't played any sessions yet"
                    : "Add your first court session to see it here"
                  : "No sessions found in the selected time period"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add Session Modal */}
      {showAddSession && (
        <AddSessionModal
          onClose={() => {
            setShowAddSession(false);
            setIsSelectingForCancel(false);
          }}
          onSessionAdded={() => {
            setShowAddSession(false);
            setIsSelectingForCancel(false);
            fetchDashboardData(); // Refresh dashboard data
          }}
          isSelectingForCancel={isSelectingForCancel}
        />
      )}

      {/* Players Modal */}
      {showPlayersModal && selectedSessionPlayers && (
        <PlayersModal
          session={selectedSessionPlayers}
          getPlayerName={getPlayerName}
          onClose={() => {
            setShowPlayersModal(false);
            setSelectedSessionPlayers(null);
          }}
        />
      )}

      {/* Calendar Date Sessions Modal */}
      {showDateModal && selectedDateSessions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Sessions on {formatDateCentral(selectedDateSessions[0].date)}
                </h3>
                <button
                  onClick={() => {
                    setShowDateModal(false);
                    setSelectedDateSessions(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {selectedDateSessions.map((session, index) => (
                  <div
                    key={session.id || index}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            session.court_type === "Wood"
                              ? "bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300"
                              : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                          }`}
                        >
                          {session.court_type} Court
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {session.hours}{" "}
                          {session.hours === 1 ? "hour" : "hours"}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-green-600 dark:text-green-400">
                        ${session.cost}
                      </div>
                    </div>

                    {session.players_present &&
                      session.players_present.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Players ({session.players_present.length}):
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {session.players_present.map((playerId) => (
                              <div
                                key={playerId}
                                className="bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600"
                              >
                                <p className="text-sm text-gray-900 dark:text-white font-medium">
                                  {getPlayerName(playerId)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {(!session.players_present ||
                      session.players_present.length === 0) && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        No players recorded for this session
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Players Screen
const PlayersScreen = ({ onPlayerClick }) => {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [userType, setUserType] = useState("");
  const [userRole, setUserRole] = useState("player");
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [showCustomizeColumns, setShowCustomizeColumns] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    name: true, // Required column - always visible
    email: true,
    user_type: true,
    balance: true,
    status: true,
  });

  // Filter states for Players page - only player filter
  const [selectedPlayerFilter, setSelectedPlayerFilter] = useState("all");

  const availableColumns = [
    { key: "name", label: "Name", sortable: true, required: true }, // Keep for reference but don't show in customization
    { key: "email", label: "Email", sortable: true },
    { key: "user_type", label: "User Type", sortable: true },
    { key: "balance", label: "Balance", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  // Only show non-required columns in customization panel
  const customizableColumns = availableColumns.filter((col) => !col.required);

  useEffect(() => {
    const determineUserTypeAndRole = async () => {
      if (!user?.uid) return;

      try {
        // Fetch user data to get user_type and role
        const response = await api.get(`/api/players/${user.uid}`);
        const playerData = response.data;

        if (playerData) {
          // Set user type (guest or regular)
          if (playerData.user_type) {
            setUserType(playerData.user_type); // Could be 'guest' or 'regular'
          } else {
            setUserType("regular"); // Default to regular if no user_type specified
          }

          // Set user role (owner, admin, or player)
          if (playerData.role) {
            setUserRole(playerData.role);
          } else {
            setUserRole("player"); // Default to player
          }
        } else {
          setUserType("regular");
          setUserRole("player");
        }
      } catch (error) {
        console.error("Error determining user type and role:", error);
        setUserType("regular");
        setUserRole("player");
      }
    };

    determineUserTypeAndRole();
  }, [user]);

  useEffect(() => {
    if (userType) {
      fetchPlayers();
    }
  }, [userType]);

  useEffect(() => {
    filterAndSortPlayers();
  }, [players, searchTerm, sortConfig, selectedPlayerFilter]);

  const fetchPlayers = async () => {
    try {
      setError("");
      const response = await api.get("/api/players");
      let playersData = response.data;

      // For guests, filter to show only their own data
      if (userType === "guest" && user?.uid) {
        playersData = playersData.filter((player) => player.id === user.uid);
      }

      setPlayers(playersData);
    } catch (error) {
      console.error("Error fetching players:", error);
      if (error.response?.status === 403) {
        setError("You do not have permission to view players.");
      } else {
        setError("Failed to load players. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPlayers = () => {
    let filtered = [...players];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (player) =>
          player.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          player.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply player filter (for admins viewing specific player)
    if (
      (userRole === "owner" || userRole === "admin") &&
      selectedPlayerFilter !== "all"
    ) {
      filtered = filtered.filter(
        (player) => player.id === selectedPlayerFilter
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle different data types
        if (sortConfig.key === "balance") {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (sortConfig.key === "joinDate") {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (sortConfig.key === "status") {
          aValue = a.active ? "Active" : "Inactive";
          bValue = b.active ? "Active" : "Inactive";
        } else if (sortConfig.key === "user_type") {
          aValue = a.user_type || "regular_player";
          bValue = b.user_type || "regular_player";
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredPlayers(filtered);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? "↑" : "↓";
    }
    return "↕";
  };

  const toggleColumnVisibility = (columnKey) => {
    // Prevent hiding required columns (like 'name')
    if (columnKey === "name") {
      return; // Name column is required and cannot be hidden
    }

    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Check if current user can add players (Only Owner, not Admin)
  const canAddPlayers = userRole === "owner";
  const canManageStatus = userRole === "owner"; // Only Owner can manage status
  const canDeletePlayers = userRole === "owner"; // Only Owner can delete players
  const canCustomizeColumns = userRole === "owner"; // Only Owner can customize columns
  const canEditPlayers = userRole === "owner"; // Only Owner can edit players

  // Get current user's player ID from Firebase
  const [currentPlayerId, setCurrentPlayerId] = useState(null);

  useEffect(() => {
    const fetchCurrentPlayerId = async () => {
      if (user?.uid) {
        setCurrentPlayerId(user.uid);
      }
    };
    fetchCurrentPlayerId();
  }, [user]);

  // Check if player can be clicked (owner/admin can click all, regular players can only click themselves)
  const canClickPlayer = (playerId) => {
    if (userRole === "owner" || userRole === "admin") return true; // Owner and Admin can click all
    // Regular players can only click their own profile
    return playerId === currentPlayerId;
  };

  const handleStatusToggle = async (playerId, currentStatus, event) => {
    event.stopPropagation(); // Prevent row click when clicking status toggle

    try {
      await api.put(`/api/players/${playerId}/status`, {
        active: !currentStatus,
      });
      // Refresh the players list to show updated status
      fetchPlayers();
    } catch (error) {
      console.error("Error updating player status:", error);
      setError("Failed to update player status. Please try again.");
    }
  };

  const handleDeletePlayer = async (playerId, playerName, event) => {
    event.stopPropagation(); // Prevent row click when clicking delete

    // Confirm deletion
    const confirmMessage = `Are you sure you want to delete ${playerName}?\n\nThis action cannot be undone and will remove:\n- Player profile\n- All attendance records\n- All payment history\n\nType 'DELETE' to confirm.`;
    const userInput = window.prompt(confirmMessage);

    if (userInput !== "DELETE") {
      return; // User cancelled or didn't type DELETE
    }

    try {
      await api.delete(`/api/players/${playerId}`);
      // Refresh the players list
      fetchPlayers();
      // Show success message
      alert(`Player "${playerName}" has been successfully deleted.`);
    } catch (error) {
      console.error("Error deleting player:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to delete player. Please try again.";
      setError(errorMessage);
      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          Loading players...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Players
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage league members ({filteredPlayers.length} players)
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* My Details Button - Visible to all users */}
          {currentPlayerId && (
            <button
              onClick={() => onPlayerClick(currentPlayerId)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              My Details
            </button>
          )}
          {canAddPlayers && (
            <button
              onClick={() => setShowAddPlayer(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              + Add Player
            </button>
          )}
        </div>
      </div>

      {/* Search and Column Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        {/* Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Player Filter (Owner/Admin only) */}
          {(userRole === "owner" || userRole === "admin") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Player
              </label>
              <select
                value={selectedPlayerFilter}
                onChange={(e) => setSelectedPlayerFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">All Players</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Search and Controls Row */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Search Field */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search players by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
          </div>

          {/* Column Customization (Admin Only) */}
          {canCustomizeColumns && (
            <button
              onClick={() => setShowCustomizeColumns(true)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Customize Columns"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
              </svg>
            </button>
          )}
        </div>
      </div>

      {showAddPlayer && (
        <AddPlayerModal
          onClose={() => setShowAddPlayer(false)}
          onPlayerAdded={() => {
            setShowAddPlayer(false);
            fetchPlayers();
          }}
        />
      )}

      {showEditUser && editingPlayer && (
        <EditUserModal
          player={editingPlayer}
          onClose={() => {
            setShowEditUser(false);
            setEditingPlayer(null);
          }}
          onUserUpdated={() => {
            setShowEditUser(false);
            setEditingPlayer(null);
            fetchPlayers(); // Refresh players list
          }}
        />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                {visibleColumns.name && (
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Player</span>
                      <span className="text-gray-400">
                        {getSortIcon("name")}
                      </span>
                    </div>
                  </th>
                )}
                {visibleColumns.email && (
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("email")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Email</span>
                      <span className="text-gray-400">
                        {getSortIcon("email")}
                      </span>
                    </div>
                  </th>
                )}
                {visibleColumns.user_type && (
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("user_type")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>User Type</span>
                      <span className="text-gray-400">
                        {getSortIcon("user_type")}
                      </span>
                    </div>
                  </th>
                )}
                {visibleColumns.balance && (
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("balance")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Balance</span>
                      <span className="text-gray-400">
                        {getSortIcon("balance")}
                      </span>
                    </div>
                  </th>
                )}
                {visibleColumns.status && userRole !== "admin" && (
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      <span className="text-gray-400">
                        {getSortIcon("status")}
                      </span>
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPlayers.map((player) => {
                const isClickable = canClickPlayer(player.id);
                return (
                  <tr
                    key={player.id}
                    className={
                      isClickable
                        ? "hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        : "cursor-default"
                    }
                    onClick={() => isClickable && onPlayerClick(player.id)}
                  >
                    {visibleColumns.name && (
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              player.role === "owner"
                                ? "bg-purple-100 dark:bg-purple-900"
                                : player.role === "admin"
                                ? "bg-indigo-100 dark:bg-indigo-900"
                                : "bg-gray-100 dark:bg-gray-700"
                            }`}
                          >
                            <span
                              className={`font-semibold ${
                                player.role === "owner"
                                  ? "text-purple-600 dark:text-purple-300"
                                  : player.role === "admin"
                                  ? "text-indigo-600 dark:text-indigo-300"
                                  : "text-gray-600 dark:text-gray-300"
                              }`}
                            >
                              {player.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase() || "?"}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {player.name || "Unknown"}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {player.role === "owner" && "👑 Owner"}
                                  {player.role === "admin" && "⚡ Admin"}
                                  {(!player.role || player.role === "player") &&
                                    "🏸 Player"}
                                </p>
                              </div>
                              {/* Edit User Icon - Only for Owner */}
                              {canEditPlayers && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click event
                                    setEditingPlayer(player);
                                    setShowEditUser(true);
                                  }}
                                  className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-200"
                                  title="Edit User"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.email && (
                      <td className="py-4 px-6">
                        <p className="text-gray-900 dark:text-white">
                          {player.email || "N/A"}
                        </p>
                      </td>
                    )}
                    {visibleColumns.user_type && (
                      <td className="py-4 px-6">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300">
                          {player.user_type === "regular_player" &&
                            "Regular Player"}
                          {player.user_type === "guest" && "Guest"}
                          {player.user_type === "other" && "Other"}
                          {!player.user_type && "Regular Player"}
                        </span>
                      </td>
                    )}
                    {visibleColumns.balance && (
                      <td className="py-4 px-6">
                        <span
                          className={`font-semibold ${
                            (player.balance || 0) >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          ${(player.balance || 0).toFixed(2)}
                        </span>
                      </td>
                    )}
                    {visibleColumns.status && userRole !== "admin" && (
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              player.active !== false
                                ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300"
                            }`}
                          >
                            {player.active !== false ? "Active" : "Inactive"}
                          </span>
                          {canManageStatus && (
                            <button
                              onClick={(e) =>
                                handleStatusToggle(
                                  player.id,
                                  player.active !== false,
                                  e
                                )
                              }
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                player.active !== false
                                  ? "text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                                  : "text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30"
                              }`}
                              title={
                                player.active !== false
                                  ? "Disable user"
                                  : "Enable user"
                              }
                            >
                              {player.active !== false ? "Disable" : "Enable"}
                            </button>
                          )}
                          {canDeletePlayers && (
                            <button
                              onClick={(e) =>
                                handleDeletePlayer(player.id, player.name, e)
                              }
                              className="px-2 py-1 text-xs font-medium rounded transition-colors text-red-600 dark:text-red-400 hover:text-white hover:bg-red-600 dark:hover:bg-red-500 border border-red-600 dark:border-red-400"
                              title="Delete player permanently (Owner only)"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary Footer */}
        {filteredPlayers.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Showing {filteredPlayers.length} players
              </span>
              <div className="flex space-x-6">
                <span className="text-gray-600 dark:text-gray-400">
                  Total Balance:{" "}
                  <span
                    className={`font-semibold ${
                      filteredPlayers.reduce(
                        (sum, player) => sum + (player.balance || 0),
                        0
                      ) >= 0
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    $
                    {filteredPlayers
                      .reduce((sum, player) => sum + (player.balance || 0), 0)
                      .toFixed(2)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* No Results Message */}
      {filteredPlayers.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">👥</span>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {searchTerm ? "No players found" : "No players yet"}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchTerm
              ? `No players match "${searchTerm}"`
              : "Add your first player to get started"}
          </p>
          {canAddPlayers && !searchTerm && (
            <button
              onClick={() => setShowAddPlayer(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Add First Player
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Add Player Modal
const AddPlayerModal = ({ onClose, onPlayerAdded }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "player",
    user_type: "regular_player",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Validate password
      if (!formData.password) {
        setError("Password is required");
        setLoading(false);
        return;
      }

      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters long");
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      // Remove confirmPassword before sending to backend
      const { confirmPassword, ...playerData } = formData;

      await api.post("/api/players", playerData);
      onPlayerAdded();
    } catch (error) {
      console.error("Error adding player:", error);
      if (error.response?.status === 403) {
        setError(
          "You do not have permission to add players. Only admins and owners can add players."
        );
      } else if (error.response?.data?.detail) {
        setError(`Failed to add player: ${error.response.data.detail}`);
      } else if (error.message) {
        setError(`Failed to add player: ${error.message}`);
      } else {
        setError("Failed to add player. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Check if current user is owner
  const isOwner = user?.email === "eltouch@gmail.com";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Add New Player
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter player name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter email address"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="player">Player</option>
              <option value="admin">Admin</option>
              {isOwner && <option value="owner">Owner</option>}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              {formData.role === "player" && "Can only view data"}
              {formData.role === "admin" &&
                "Can log attendance and update funds"}
              {formData.role === "owner" &&
                "Can do everything (read, write, delete, edit users)"}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Type
            </label>
            <select
              name="user_type"
              value={formData.user_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="regular_player">Regular Player</option>
              <option value="guest">Guest</option>
              <option value="other">Other</option>
            </select>
            <p className="text-xs text-gray-600 mt-1">
              Regular players are league members, guests are temporary players,
              others are special cases
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password *
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter password (min 6 characters)"
              required
              minLength={6}
            />
            <p className="text-xs text-gray-600 mt-1">
              This password will be shared with the user manually
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password *
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Confirm password"
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="showPasswordAddPlayer"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label
              htmlFor="showPasswordAddPlayer"
              className="ml-2 block text-sm text-gray-700"
            >
              Show passwords
            </label>
          </div>

          <div className="flex space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Player"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal
const EditUserModal = ({ player, onClose, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    name: player?.name || "",
    email: player?.email || "",
    role: player?.role || "player",
    user_type: player?.user_type || "regular_player", // Add user_type
    active: player?.active !== false,
    password: "", // New password (optional)
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Validate password confirmation if password is provided
      if (formData.password && formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        user_type: formData.user_type, // Include user_type
        active: formData.active,
      };

      // Only include password if it's provided
      if (formData.password.trim()) {
        updateData.password = formData.password;
      }

      await api.put(`/api/players/${player.id}`, updateData);
      onUserUpdated();
    } catch (error) {
      console.error("Error updating user:", error);
      if (error.response?.data?.detail) {
        setError(`Failed to update user: ${error.response.data.detail}`);
      } else {
        setError("Failed to update user. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit User Information
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="player">Player</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              User Type
            </label>
            <select
              name="user_type"
              value={formData.user_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="regular_player">Regular Player</option>
              <option value="guest">Guest</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Active User
            </label>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Change Password (Optional)
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {formData.password && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              {formData.password && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPasswordEditUser"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="showPasswordEditUser"
                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                  >
                    Show passwords
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
            >
              {loading ? "Updating..." : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Attendance Screen
const AttendanceScreen = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSession, setShowAddSession] = useState(false);
  const [userRole, setUserRole] = useState("player");
  const [userType, setUserType] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showEditSession, setShowEditSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [selectedSessionPlayers, setSelectedSessionPlayers] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  // Filter states
  const [selectedFilter, setSelectedFilter] = useState("thisMonth");
  const [selectedPlayer, setSelectedPlayer] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDate, setShowCustomDate] = useState(false);

  useEffect(() => {
    // Determine user role
    const determineUserRole = async () => {
      if (!user?.uid) return;

      try {
        // Check if user is the owner
        if (user?.email === "eltouch@gmail.com") {
          setUserRole("owner");
          return;
        }

        // For other users, fetch their role from the players collection
        const response = await api.get(`/api/players/${user.uid}`);
        const playerData = response.data;

        // Set user type
        if (playerData && playerData.user_type) {
          setUserType(playerData.user_type);
        }

        if (playerData && playerData.role) {
          setUserRole(playerData.role); // Could be 'admin' or 'player'
          // For guests, always default to their own attendance (they can't change it)
          // For regular players, default to their own attendance (they can change it)
          if (
            playerData.user_type === "guest" ||
            playerData.role === "player"
          ) {
            setSelectedPlayer(user.uid);
          }
        } else {
          setUserRole("player"); // Default to player if no role specified
          // Default to user's own attendance
          setSelectedPlayer(user.uid);
        }
      } catch (error) {
        console.error("Error determining user role:", error);
        setUserRole("player");
        // Default to user's own attendance on error
        setSelectedPlayer(user.uid);
      }
    };

    determineUserRole();
  }, [user]);

  useEffect(() => {
    fetchSessions();
    // Fetch players for all users so they can see player names in sessions
    fetchPlayers();
  }, [userRole]);

  useEffect(() => {
    applyFilters();
  }, [
    sessions,
    selectedFilter,
    selectedPlayer,
    customStartDate,
    customEndDate,
  ]);

  const fetchSessions = async () => {
    try {
      const response = await api.get("/api/attendance");
      let allSessions = response.data;

      // Fetch all sessions for everyone - filtering happens in applyFilters based on selectedPlayer dropdown
      setSessions(allSessions);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await api.get("/api/players");
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const getDateRange = (filter) => {
    const now = new Date();
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    );

    switch (filter) {
      case "thisMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
        };
      case "lastMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
        };
      case "thisWeek":
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return {
          start: startOfWeek,
          end: endOfDay,
        };
      case "lastWeek":
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);
        return {
          start: lastWeekStart,
          end: lastWeekEnd,
        };
      case "last6Months":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 6, 1),
          end: endOfDay,
        };
      case "thisYear":
        return {
          start: new Date(now.getFullYear(), 0, 1),
          end: endOfDay,
        };
      case "lastYear":
        return {
          start: new Date(now.getFullYear() - 1, 0, 1),
          end: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59),
        };
      case "custom":
        return {
          start: customStartDate
            ? new Date(customStartDate)
            : new Date(2020, 0, 1),
          end: customEndDate ? new Date(customEndDate + "T23:59:59") : endOfDay,
        };
      default:
        return { start: new Date(2020, 0, 1), end: endOfDay };
    }
  };

  const applyFilters = () => {
    let filtered = [...sessions];

    // Date filter
    if (selectedFilter !== "all") {
      const { start, end } = getDateRange(selectedFilter);
      filtered = filtered.filter((session) => {
        // Parse date without timezone conversion
        const sessionDate = new Date(session.date + "T12:00:00");
        return sessionDate >= start && sessionDate <= end;
      });
    }

    // Player filter - works for all users (not just owner/admin)
    // Guests can only see their own attendance (handled by selectedPlayer being locked to their uid)
    if (selectedPlayer !== "all") {
      filtered = filtered.filter(
        (session) =>
          session.players_present &&
          session.players_present.includes(selectedPlayer)
      );
    }

    setFilteredSessions(filtered);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedSessions = [...filteredSessions].sort((a, b) => {
      if (key === "date") {
        // Parse date without timezone conversion
        const dateA = new Date(a[key] + "T12:00:00");
        const dateB = new Date(b[key] + "T12:00:00");
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (key === "total_cost" || key === "hours") {
        return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
      }

      if (key === "players_count") {
        const countA =
          (a.players_present?.length || 0) + (a.guests_present?.length || 0);
        const countB =
          (b.players_present?.length || 0) + (b.guests_present?.length || 0);
        return direction === "asc" ? countA - countB : countB - countA;
      }

      const valueA = a[key]?.toString().toLowerCase() || "";
      const valueB = b[key]?.toString().toLowerCase() || "";

      if (direction === "asc") {
        return valueA.localeCompare(valueB);
      }
      return valueB.localeCompare(valueA);
    });

    setFilteredSessions(sortedSessions);
  };

  const exportToCSV = () => {
    if (!filteredSessions.length) return;

    const csvData = [
      [
        "Date",
        "Court Type",
        "Hours",
        "Players",
        "Guests",
        "Total Cost",
        "Cost Per Person",
      ],
    ];

    filteredSessions.forEach((session) => {
      csvData.push([
        formatDateCentral(session.date),
        session.court_type || "",
        session.hours || 0,
        session.players_present?.length || 0,
        session.guests_present?.length || 0,
        `$${(session.total_cost || 0).toFixed(2)}`,
        `$${(session.cost_per_person || 0).toFixed(2)}`,
      ]);
    });

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const playerName =
      selectedPlayer !== "all"
        ? players.find((p) => p.id === selectedPlayer)?.name || "Unknown"
        : "All";

    a.download = `${playerName}_Attendance_${selectedFilter}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print function
  const handlePrint = () => {
    if (!filteredSessions.length) return;

    const playerName =
      selectedPlayer !== "all"
        ? players.find((p) => p.id === selectedPlayer)?.name || "Unknown"
        : "All Players";

    const printWindow = window.open("", "_blank");
    const printContent = `
      <html>
        <head>
          <title>Attendance Report - ${playerName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .cost { font-weight: bold; color: #16a34a; }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Attendance Report - ${playerName}</h1>
          <p>Filter: ${getFilterTitle()}</p>
          <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <p>Total Sessions: ${filteredSessions.length}</p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Court Type</th>
                <th>Hours</th>
                <th>Players</th>
                <th>Guests</th>
                <th>Total Cost</th>
                <th>Cost/Person</th>
              </tr>
            </thead>
            <tbody>
              ${filteredSessions
                .map(
                  (session) => `
                <tr>
                  <td>${formatDateCentral(session.date)}</td>
                  <td>${session.court_type || "N/A"}</td>
                  <td>${session.hours || 0}</td>
                  <td>${session.players_present?.length || 0}</td>
                  <td>${session.guests_present?.length || 0}</td>
                  <td class="cost">$${(session.total_cost || 0).toFixed(2)}</td>
                  <td class="cost">$${(session.cost_per_person || 0).toFixed(
                    2
                  )}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getFilterTitle = () => {
    switch (selectedFilter) {
      case "thisMonth":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      case "thisWeek":
        return "This Week";
      case "lastWeek":
        return "Last Week";
      case "last6Months":
        return "Last 6 Months";
      case "thisYear":
        return "This Year";
      case "lastYear":
        return "Last Year";
      case "custom":
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  const getPageTitle = () => {
    if (selectedPlayer !== "all") {
      const player = players.find((p) => p.id === selectedPlayer);
      return `${player?.name || "Player"}'s Attendance`;
    }
    return "Attendance";
  };

  const handleDeleteSession = async (sessionId, event) => {
    event.stopPropagation(); // Prevent any row click events

    if (
      !window.confirm(
        "Are you sure you want to delete this session? This will reverse balance changes for all participants."
      )
    ) {
      return;
    }

    try {
      console.log("[DELETE SESSION] Deleting session:", sessionId);
      const response = await api.delete(`/api/attendance/${sessionId}`);
      console.log("[DELETE SESSION] Response:", response.data);

      // Refresh sessions list
      await fetchSessions();
      alert("Session deleted successfully!");
    } catch (error) {
      console.error("[DELETE SESSION] Error deleting session:", error);
      console.error("[DELETE SESSION] Error details:", error.response?.data);

      const errorMessage =
        error.response?.data?.detail ||
        "Failed to delete session. Please try again.";
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleEditSession = (session, event) => {
    event.stopPropagation(); // Prevent any row click events
    setEditingSession(session);
    setShowEditSession(true);
  };

  const handleShowPlayers = (session, event) => {
    event.stopPropagation(); // Prevent any row click events
    setSelectedSessionPlayers(session);
    setShowPlayersModal(true);
  };

  const getPlayerName = (playerId) => {
    const player = players.find((p) => p.id === playerId);
    return player ? player.name : "Unknown Player";
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400">⇅</span>;
    }
    return sortConfig.direction === "asc" ? (
      <span className="text-indigo-600">↑</span>
    ) : (
      <span className="text-indigo-600">↓</span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          Loading attendance...
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {getPageTitle()}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {getFilterTitle()} • {filteredSessions.length} sessions
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Export Options - Icon Only */}
          {filteredSessions.length > 0 && (
            <>
              <button
                onClick={exportToCSV}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Export CSV"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </button>

              <button
                onClick={handlePrint}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Print"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                </svg>
              </button>
            </>
          )}

          {/* Add Session Button */}
          {(userRole === "owner" || userRole === "admin") && (
            <button
              onClick={() => setShowAddSession(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              + Add Session
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Period
            </label>
            <select
              value={selectedFilter}
              onChange={(e) => {
                setSelectedFilter(e.target.value);
                setShowCustomDate(e.target.value === "custom");
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="thisWeek">This Week</option>
              <option value="lastWeek">Last Week</option>
              <option value="last6Months">Last 6 Months</option>
              <option value="thisYear">This Year Till Date</option>
              <option value="lastYear">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {showCustomDate && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </>
          )}

          {/* Player Filter - Hidden for guests, they can only see their own attendance */}
          {userType !== "guest" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Player
              </label>
              <select
                value={selectedPlayer}
                onChange={(e) => setSelectedPlayer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">All Players</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Data Grid */}
      {filteredSessions.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort("date")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date</span>
                      {getSortIcon("date")}
                    </div>
                  </th>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort("court_type")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Court</span>
                      {getSortIcon("court_type")}
                    </div>
                  </th>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort("hours")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Hours</span>
                      {getSortIcon("hours")}
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Player Names
                  </th>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort("total_cost")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Cost</span>
                      {getSortIcon("total_cost")}
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Cost/Person
                  </th>
                  {(userRole === "owner" || userRole === "admin") && (
                    <th className="text-right py-4 px-6 font-semibold text-gray-900 dark:text-white">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSessions.map((session, index) => (
                  <tr
                    key={session.id || index}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors relative"
                    onMouseEnter={() => setHoveredRow(session.id || index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {formatDateCentral(session.date)}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          session.court_type === "Wood"
                            ? "bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300"
                            : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                        }`}
                      >
                        {session.court_type}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white">
                        {session.hours}h
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <button
                        onClick={(e) => handleShowPlayers(session, e)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline cursor-pointer transition-colors"
                      >
                        {(session.players_present?.length || 0) +
                          (session.guests_present?.length || 0) ===
                        1
                          ? "1 player"
                          : `${
                              (session.players_present?.length || 0) +
                              (session.guests_present?.length || 0)
                            } players`}
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        ${(session.total_cost || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-700 dark:text-gray-300">
                        ${(session.cost_per_person || 0).toFixed(2)}
                      </span>
                    </td>
                    {(userRole === "owner" || userRole === "admin") && (
                      <td className="py-4 px-6">
                        <div
                          className={`flex items-center justify-end space-x-2 transition-opacity ${
                            hoveredRow === (session.id || index)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        >
                          <button
                            onClick={(e) => handleEditSession(session, e)}
                            className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                            title="Edit Session"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                            </svg>
                          </button>
                          <button
                            onClick={(e) => handleDeleteSession(session.id, e)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete Session"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Showing {filteredSessions.length} sessions
              </span>
              <div className="flex space-x-6">
                <span className="text-gray-600 dark:text-gray-400">
                  Total Hours:{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {filteredSessions
                      .reduce((sum, session) => sum + (session.hours || 0), 0)
                      .toFixed(1)}
                  </span>
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {userRole === "player" || userRole === "guest"
                    ? "My Total Cost"
                    : "Total Cost"}
                  :{" "}
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    $
                    {filteredSessions
                      .reduce(
                        (sum, session) => sum + (session.cost_per_person || 0),
                        0
                      )
                      .toFixed(2)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <span className="text-6xl mb-4 block">🏸</span>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No sessions found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {selectedFilter !== "all"
              ? "Try adjusting your filters"
              : "Add your first session to get started"}
          </p>
        </div>
      )}

      {/* Add Session Modal */}
      {showAddSession && (
        <AddSessionModal
          onClose={() => setShowAddSession(false)}
          onSessionAdded={() => {
            setShowAddSession(false);
            fetchSessions();
          }}
        />
      )}

      {/* Edit Session Modal */}
      {showEditSession && editingSession && (
        <EditSessionModal
          session={editingSession}
          onClose={() => {
            setShowEditSession(false);
            setEditingSession(null);
          }}
          onSessionUpdated={() => {
            setShowEditSession(false);
            setEditingSession(null);
            fetchSessions();
          }}
        />
      )}

      {/* Players Modal */}
      {showPlayersModal && selectedSessionPlayers && (
        <PlayersModal
          session={selectedSessionPlayers}
          getPlayerName={getPlayerName}
          onClose={() => {
            setShowPlayersModal(false);
            setSelectedSessionPlayers(null);
          }}
        />
      )}
    </div>
  );
};

// Edit Session Modal
const EditSessionModal = ({ session, onClose, onSessionUpdated }) => {
  const [players, setPlayers] = useState([]);
  const [formData, setFormData] = useState({
    date: session.date
      ? new Date(session.date + "T12:00:00").toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    court_type: session.court_type || "Green",
    hours: session.hours || 2.0,
    players_present: session.players_present || [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await api.get("/api/players/active");
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching active players:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const sessionData = {
        date: formData.date,
        court_type: formData.court_type,
        hours: formData.hours,
        players_present: formData.players_present,
        guests_present: session.guests_present || [],
        total_cost: 0,
        cost_per_person: 0,
      };

      // Note: Backend doesn't have a PUT endpoint yet, so we'll delete and recreate
      // First delete the old session
      await api.delete(`/api/attendance/${session.id}`);

      // Then create the new session
      await api.post("/api/attendance", sessionData);

      onSessionUpdated();
    } catch (error) {
      console.error("Error updating session:", error);
      if (error.response?.data?.detail) {
        setError(`Failed to update session: ${error.response.data.detail}`);
      } else {
        setError("Failed to update session. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "hours" ? parseFloat(value) : value,
    });
  };

  const handlePlayerToggle = (playerId) => {
    setFormData((prev) => ({
      ...prev,
      players_present: prev.players_present.includes(playerId)
        ? prev.players_present.filter((id) => id !== playerId)
        : [...prev.players_present, playerId],
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setFormData((prev) => ({
        ...prev,
        players_present: [],
      }));
      setSelectAll(false);
    } else {
      const allPlayerIds = players.map((player) => player.id);
      setFormData((prev) => ({
        ...prev,
        players_present: allPlayerIds,
      }));
      setSelectAll(true);
    }
  };

  useEffect(() => {
    if (players.length > 0) {
      const allSelected = players.every((player) =>
        formData.players_present.includes(player.id)
      );
      setSelectAll(allSelected);
    }
  }, [formData.players_present, players]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Attendance Session
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Court Type
              </label>
              <select
                name="court_type"
                value={formData.court_type}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="Wood">Wood Court</option>
                <option value="Green">Green Court</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Hours
              </label>
              <select
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value={1.5}>1.5 hours</option>
                <option value={2.0}>2 hours</option>
                <option value={2.5}>2.5 hours</option>
                <option value={3.0}>3 hours</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Players Present
              </label>
              <label className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Select All</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
              {players.map((player) => (
                <label
                  key={player.id}
                  className="flex items-center space-x-3 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <input
                    type="checkbox"
                    checked={formData.players_present.includes(player.id)}
                    onChange={() => handlePlayerToggle(player.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-300">
                        {player.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {player.name}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {formData.players_present.length} players selected
            </p>
          </div>

          <div className="flex space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.players_present.length === 0}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update Session"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Players Modal - Shows list of players present in a session
const PlayersModal = ({ session, getPlayerName, onClose }) => {
  const allPlayers = [
    ...(session.players_present || []).map((id) => ({ id, type: "player" })),
    ...(session.guests_present || []).map((id) => ({ id, type: "guest" })),
  ];

  const getInitials = (name) => {
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (index) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-yellow-500",
      "bg-red-500",
      "bg-teal-500",
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Players Present
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {formatDateCentral(session.date)} • {session.court_type} Court •{" "}
            {session.hours}h
          </p>
        </div>

        {/* Players List */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {allPlayers.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No players in this session
            </div>
          ) : (
            <div className="space-y-3">
              {allPlayers.map((player, index) => {
                const playerName = getPlayerName(player.id);
                return (
                  <div
                    key={`${player.type}-${player.id}`}
                    className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {/* Avatar */}
                    <div
                      className={`w-10 h-10 rounded-full ${getAvatarColor(
                        index
                      )} flex items-center justify-center text-white font-semibold`}
                    >
                      {getInitials(playerName)}
                    </div>

                    {/* Name and Type */}
                    <div className="flex-1">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {playerName}
                      </p>
                      {player.type === "guest" && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300">
                          Guest
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              Total: {allPlayers.length}{" "}
              {allPlayers.length === 1 ? "player" : "players"}
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              Cost per person: ${(session.cost_per_person || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add Session Modal
const AddSessionModal = ({
  onClose,
  onSessionAdded,
  isSelectingForCancel = false,
}) => {
  const [players, setPlayers] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    court_type: "Green", // Changed default from 'Wood' to 'Green'
    hours: 2.0, // Confirmed default is 2 hours
    players_present: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchPlayers();
  }, []);

  // Auto-select all players when in "Cancel Today's" mode
  useEffect(() => {
    if (isSelectingForCancel && players.length > 0) {
      const allPlayerIds = players.map((player) => player.id);
      setFormData((prev) => ({
        ...prev,
        players_present: allPlayerIds,
      }));
      setSelectAll(true);
    }
  }, [isSelectingForCancel, players]);

  const fetchPlayers = async () => {
    try {
      const response = await api.get("/api/players/active");
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching active players:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Structure the data to match the backend AttendanceSession model
      const sessionData = {
        date: formData.date,
        court_type: formData.court_type,
        hours: formData.hours,
        players_present: formData.players_present,
        guests_present: [], // Empty array for guests
        total_cost: 0, // Backend will calculate this
        cost_per_person: 0, // Backend will calculate this
      };

      await api.post("/api/attendance", sessionData);
      onSessionAdded();
    } catch (error) {
      console.error("Error adding session:", error);
      if (error.response?.data?.detail) {
        setError(`Failed to add session: ${error.response.data.detail}`);
      } else {
        setError("Failed to add session. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "hours" ? parseFloat(value) : value,
    });
  };

  const handlePlayerToggle = (playerId) => {
    setFormData((prev) => ({
      ...prev,
      players_present: prev.players_present.includes(playerId)
        ? prev.players_present.filter((id) => id !== playerId)
        : [...prev.players_present, playerId],
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setFormData((prev) => ({
        ...prev,
        players_present: [],
      }));
      setSelectAll(false);
    } else {
      // Select all active players
      const allPlayerIds = players.map((player) => player.id);
      setFormData((prev) => ({
        ...prev,
        players_present: allPlayerIds,
      }));
      setSelectAll(true);
    }
  };

  // Update selectAll state when players are individually selected/deselected
  useEffect(() => {
    if (players.length > 0) {
      const allSelected = players.every((player) =>
        formData.players_present.includes(player.id)
      );
      setSelectAll(allSelected);
    }
  }, [formData.players_present, players]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {isSelectingForCancel
              ? "Select all players in the list who are active"
              : "Add Attendance Session"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Court Type
              </label>
              <select
                name="court_type"
                value={formData.court_type}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="Wood">Wood Court</option>
                <option value="Green">Green Court</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hours
              </label>
              <select
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value={0.5}>0.5 hours</option>
                <option value={1.0}>1 hour</option>
                <option value={1.5}>1.5 hours</option>
                <option value={2.0}>2 hours</option>
                <option value={2.5}>2.5 hours</option>
                <option value={3.0}>3 hours</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Select Players Present
              </label>
              <label className="flex items-center space-x-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Select All</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {players.map((player) => (
                <label
                  key={player.id}
                  className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.players_present.includes(player.id)}
                    onChange={() => handlePlayerToggle(player.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-indigo-600">
                        {player.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {player.name}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {formData.players_present.length} players selected
            </p>
          </div>

          <div className="flex space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.players_present.length === 0}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Session"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Individual Player Detail Screen
const PlayerDetailScreen = ({ playerId, onBack, setCurrentScreen }) => {
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [playerSessions, setPlayerSessions] = useState([]);
  const [playerPayments, setPlayerPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showSendReminderModal, setShowSendReminderModal] = useState(false);
  const [userRole, setUserRole] = useState("player");

  // Check if current user is owner
  const isOwner = user?.email === "eltouch@gmail.com";

  // Fetch current user's role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.uid) return;

      try {
        const response = await api.get(`/api/players/${user.uid}`);
        const userData = response.data;
        if (userData && userData.role) {
          setUserRole(userData.role);
        } else {
          setUserRole("player");
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        setUserRole("player");
      }
    };

    fetchUserRole();
  }, [user]);

  useEffect(() => {
    if (playerId) {
      fetchPlayerData();
    }
  }, [playerId]);

  const fetchPlayerData = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("PlayerDetailScreen: Fetching data for playerId:", playerId);

      // Fetch player details
      console.log("PlayerDetailScreen: Calling /api/players/" + playerId);
      const playerResponse = await api.get(`/api/players/${playerId}`);
      console.log("PlayerDetailScreen: Player response:", playerResponse.data);
      setPlayer(playerResponse.data);

      // Fetch player's attendance sessions
      console.log("PlayerDetailScreen: Fetching attendance sessions");
      const sessionsResponse = await api.get("/api/attendance");
      const allSessions = sessionsResponse.data;
      const playerSessionsFiltered = allSessions.filter(
        (session) =>
          session.players_present && session.players_present.includes(playerId)
      );
      console.log(
        "PlayerDetailScreen: Filtered sessions:",
        playerSessionsFiltered.length
      );
      setPlayerSessions(playerSessionsFiltered);

      // Fetch player's payments
      console.log("PlayerDetailScreen: Fetching payments");
      const paymentsResponse = await api.get("/api/payments");
      const allPayments = paymentsResponse.data;
      const playerPaymentsFiltered = allPayments.filter(
        (payment) => payment.player_id === playerId
      );
      console.log(
        "PlayerDetailScreen: Filtered payments:",
        playerPaymentsFiltered.length
      );
      setPlayerPayments(playerPaymentsFiltered);

      console.log("PlayerDetailScreen: All data fetched successfully");
    } catch (error) {
      console.error("PlayerDetailScreen: Error fetching player data:", error);
      console.error("PlayerDetailScreen: Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });
      setError("Failed to load player details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading player details...</div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || "Player not found"}
        </div>
      </div>
    );
  }

  const totalSessions = playerSessions.length;
  const totalHours = playerSessions.reduce(
    (sum, session) => sum + (session.hours || 0),
    0
  );
  const totalCost = playerSessions.reduce(
    (sum, session) => sum + (session.cost_per_person || 0),
    0
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center mb-8">
        <button
          onClick={onBack}
          className="mr-3 p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition duration-200"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M15 19l-7-7 7-7"></path>
          </svg>
        </button>
        <div className="flex items-center space-x-4">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center ${
              player.role === "owner"
                ? "bg-purple-100"
                : player.role === "admin"
                ? "bg-indigo-100"
                : "bg-gray-100"
            }`}
          >
            <span
              className={`text-2xl font-semibold ${
                player.role === "owner"
                  ? "text-purple-600"
                  : player.role === "admin"
                  ? "text-indigo-600"
                  : "text-gray-600"
              }`}
            >
              {player.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{player.name}</h1>
            <p className="text-gray-600">{player.email}</p>
          </div>
        </div>
      </div>

      {/* Player Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Balance</p>
              <p
                className={`text-3xl font-bold ${
                  player.balance >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                ${player.balance.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Sessions</p>
              <p className="text-3xl font-bold text-gray-900">
                {totalSessions}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🏸</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Hours</p>
              <p className="text-3xl font-bold text-gray-900">
                {totalHours.toFixed(1)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">⏰</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Cost</p>
              <p className="text-3xl font-bold text-gray-900">
                ${totalCost.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💵</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Last Sign In</p>
              <p className="text-lg font-semibold text-gray-900">
                {player.lastSignIn
                  ? new Date(player.lastSignIn).toLocaleDateString()
                  : "Never"}
              </p>
              <p className="text-xs text-gray-500">
                {player.lastSignIn
                  ? new Date(player.lastSignIn).toLocaleTimeString()
                  : "No login recorded"}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🔐</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-8">
        {(userRole === "owner" || userRole === "admin") && (
          <button
            onClick={() => setShowAddPayment(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition duration-200"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            <span>Add Payment</span>
          </button>
        )}

        {isOwner && (
          <button
            onClick={() => setShowEditUser(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition duration-200"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            <span>Edit User</span>
          </button>
        )}

        <button
          onClick={() => {
            if (setCurrentScreen) {
              // Navigate to reports with player filter
              setCurrentScreen("reports", { selectedPlayer: playerId });
            }
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition duration-200"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          <span>View Reports</span>
        </button>

        {(userRole === "owner" || userRole === "admin") && (
          <button
            onClick={() => setShowSendReminderModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition duration-200"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            <span>Send Payment Reminder</span>
          </button>
        )}
      </div>

      {/* Recent Sessions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Recent Sessions
          </h2>
        </div>
        <div className="p-6">
          {playerSessions.length > 0 ? (
            <div className="space-y-4">
              {playerSessions.slice(0, 5).map((session, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <span className="text-lg">🏸</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {session.court_type} Court - {session.hours}h
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatDateCentral(session.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      ${(session.total_cost || 0).toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600">
                      ${(session.cost_per_person || 0).toFixed(2)}/person
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <span className="text-4xl mb-4 block">🏸</span>
              <p>No sessions attended yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Payment History
          </h2>
        </div>
        <div className="p-6">
          {playerPayments.length > 0 ? (
            <div className="space-y-4">
              {playerPayments.slice(0, 5).map((payment, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-lg">💰</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {payment.description}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatPaymentDate(payment.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">
                      +${payment.amount.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <span className="text-4xl mb-4 block">💰</span>
              <p>No payments recorded yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Payment Modal */}
      {showAddPayment && (
        <AddPaymentModal
          players={[player]} // Pass current player
          onClose={() => setShowAddPayment(false)}
          onPaymentAdded={() => {
            setShowAddPayment(false);
            fetchPlayerData(); // Refresh player data
          }}
        />
      )}

      {/* Edit User Modal */}
      {showEditUser && (
        <EditUserModal
          player={player}
          onClose={() => setShowEditUser(false)}
          onUserUpdated={() => {
            setShowEditUser(false);
            fetchPlayerData(); // Refresh player data
          }}
        />
      )}

      {/* Send Payment Reminder Modal */}
      {showSendReminderModal && (
        <SendPaymentReminderModal
          player={player}
          onClose={() => setShowSendReminderModal(false)}
        />
      )}
    </div>
  );
};

// Send Payment Reminder Modal
const SendPaymentReminderModal = ({ player, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!player) return null;

  const handleSend = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      console.log(`Sending reminder to player: ${player.id}`);
      const response = await api.post(`/api/players/${player.id}/send-reminder`);
      setSuccess(response.data.message || "Payment reminder sent successfully!");
    } catch (err) {
      console.error("Error sending payment reminder:", err);
      setError(err.message || "Failed to send payment reminder email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Send Payment Reminder
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm">
              {success}
            </div>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition duration-200"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-gray-600 dark:text-gray-300 text-sm">
              <p className="mb-4">
                This will send an email to <strong>{player.name}</strong> (<strong>{player.email}</strong>) with the current balance of <strong className={player.balance < 0 ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"}>${(player.balance || 0).toFixed(2)}</strong> and the payment reminder.
              </p>
              <p>
                Are you sure you want to proceed?
              </p>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition duration-200 disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  "Send Reminder"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Payments Screen
const PaymentsScreen = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState("player");
  const [userType, setUserType] = useState("");
  const [currentBalance, setCurrentBalance] = useState(0);
  const [paymentFilter, setPaymentFilter] = useState("personal"); // 'personal', 'players', or 'ghmbl'
  const [sortField, setSortField] = useState("date"); // field to sort by
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc' or 'desc'

  // GHMBL Balance states
  const [ghmblBalance, setGhmblBalance] = useState(0);
  const [isEditingBalance, setIsEditingBalance] = useState(false);
  const [tempBalance, setTempBalance] = useState("");

  // Edit/Delete functionality states
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Filter states for Payments page
  const [selectedTimePeriodPayments, setSelectedTimePeriodPayments] =
    useState("thisMonth");
  const [selectedPlayerPayments, setSelectedPlayerPayments] = useState("all");
  const [customStartDatePayments, setCustomStartDatePayments] = useState("");
  const [customEndDatePayments, setCustomEndDatePayments] = useState("");
  const [showCustomDatePayments, setShowCustomDatePayments] = useState(false);
  const [filteredPayments, setFilteredPayments] = useState([]);

  // Virtual scrolling states
  const [scrollTop, setScrollTop] = useState(0);
  const ROW_HEIGHT = 72; // Height of each table row in pixels
  const VISIBLE_ROWS = 10; // Number of rows visible in viewport
  const BUFFER_ROWS = 3; // Extra rows to render above/below viewport

  useEffect(() => {
    // Determine user role and fetch appropriate data
    const determineUserRole = async () => {
      if (!user?.uid) return;

      try {
        // Check if user is the owner
        if (user?.email === "eltouch@gmail.com") {
          setUserRole("owner");
          return;
        }

        // For other users, fetch their role from the players collection
        const response = await api.get(`/api/players/${user.uid}`);
        const playerData = response.data;

        // Set user type
        if (playerData && playerData.user_type) {
          setUserType(playerData.user_type);
        }

        if (playerData && playerData.role) {
          setUserRole(playerData.role); // Could be 'admin' or 'player'
          // For regular players (not guests), default to their own payments
          if (
            playerData.role === "player" &&
            playerData.user_type !== "guest"
          ) {
            setSelectedPlayerPayments(user.uid);
          }
          // For guests, default to their own payments (they can't change it)
          if (playerData.user_type === "guest") {
            setSelectedPlayerPayments(user.uid);
          }
        } else {
          setUserRole("player"); // Default to player if no role specified
          setSelectedPlayerPayments(user.uid);
        }
      } catch (error) {
        console.error("Error determining user role:", error);
        setUserRole("player");
      }
    };

    determineUserRole();
  }, [user]);

  useEffect(() => {
    if (!user) return; // Wait for user to be loaded

    fetchPayments();
    fetchGhmblBalance(); // Fetch GHMBL balance for all users
    fetchCurrentBalance(); // Fetch current user's balance for "My Balance" display

    // Fetch players for Owner, Admin, and Regular Players (not guests)
    if (
      user?.email === "eltouch@gmail.com" ||
      userRole === "owner" ||
      userRole === "admin" ||
      (userRole === "player" && userType !== "guest")
    ) {
      fetchPlayers();
    }
  }, [user, userRole, userType, paymentFilter]); // Add userType and paymentFilter as dependencies

  // Apply filters when payments or filter criteria change
  useEffect(() => {
    applyPaymentFilters();
  }, [
    payments,
    selectedTimePeriodPayments,
    selectedPlayerPayments,
    customStartDatePayments,
    customEndDatePayments,
  ]);

  const getDateRangePayments = (filter) => {
    const now = new Date();
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    );

    switch (filter) {
      case "thisMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
        };
      case "lastMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
        };
      case "thisWeek":
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return { start: startOfWeek, end: endOfDay };
      case "lastWeek":
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59);
        return { start: lastWeekStart, end: lastWeekEnd };
      case "last6Months":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 6, 1),
          end: endOfDay,
        };
      case "thisYear":
        return {
          start: new Date(now.getFullYear(), 0, 1),
          end: endOfDay,
        };
      case "lastYear":
        return {
          start: new Date(now.getFullYear() - 1, 0, 1),
          end: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59),
        };
      case "custom":
        return {
          start: customStartDatePayments
            ? new Date(customStartDatePayments)
            : startOfDay,
          end: customEndDatePayments
            ? new Date(customEndDatePayments + "T23:59:59")
            : endOfDay,
        };
      default:
        return { start: null, end: null };
    }
  };

  const applyPaymentFilters = () => {
    let filtered = [...payments];

    // Apply time period filter
    if (selectedTimePeriodPayments !== "all") {
      const { start, end } = getDateRangePayments(selectedTimePeriodPayments);
      if (start && end) {
        filtered = filtered.filter((payment) => {
          const paymentDate = new Date(payment.date);
          return paymentDate >= start && paymentDate <= end;
        });
      }
    }

    // Apply player filter for all users (except guests who are restricted)
    if (selectedPlayerPayments !== "all") {
      filtered = filtered.filter(
        (payment) => payment.player_id === selectedPlayerPayments
      );
    }

    setFilteredPayments(filtered);
  };

  const fetchCurrentBalance = async () => {
    try {
      const response = await api.get(`/api/players/${user?.uid}`);
      setCurrentBalance(response.data.balance || 0);
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  const fetchGhmblBalance = async () => {
    try {
      const response = await api.get("/api/system/ghmbl-balance");
      setGhmblBalance(response.data.balance || 0);
    } catch (error) {
      console.error("Error fetching GHMBL balance:", error);
    }
  };

  const handleSaveGhmblBalance = async () => {
    try {
      const newBalance = parseFloat(tempBalance);
      if (isNaN(newBalance)) {
        alert("Please enter a valid number");
        return;
      }

      await api.put("/api/system/ghmbl-balance", null, {
        params: { balance: newBalance },
      });

      setGhmblBalance(newBalance);
      setIsEditingBalance(false);
      setTempBalance("");
    } catch (error) {
      console.error("Error updating GHMBL balance:", error);
      alert("Failed to update GHMBL balance");
    }
  };

  // Helper function to get payment type icon
  const getPaymentTypeIcon = (paymentType) => {
    switch (paymentType?.toLowerCase()) {
      case "venmo":
        return (
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">V</span>
            </div>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              Venmo
            </span>
          </div>
        );
      case "zelle":
        return (
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-purple-600 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">Z</span>
            </div>
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
              Zelle
            </span>
          </div>
        );
      case "cash":
        return (
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">$</span>
            </div>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              Cash
            </span>
          </div>
        );
      case "check":
        return (
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-gray-600 rounded flex items-center justify-center">
              <span className="text-white text-xs">✓</span>
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Check
            </span>
          </div>
        );
      case "manual":
      default:
        return (
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-indigo-600 rounded flex items-center justify-center">
              <span className="text-white text-xs">M</span>
            </div>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              Manual
            </span>
          </div>
        );
    }
  };

  const fetchPayments = async () => {
    try {
      setError("");
      let response;

      if (userType === "guest") {
        // Guests: Fetch only personal payments
        response = await api.get("/api/payments/personal");
      } else if (userRole === "player" && userType !== "guest") {
        // Regular players: Fetch all payments (filtering happens via dropdown)
        response = await api.get("/api/payments");
      } else {
        // Owner/Admin: Fetch payments based on filter
        if (paymentFilter === "personal") {
          // Fetch owner's personal payments
          response = await api.get("/api/payments/personal");
        } else if (paymentFilter === "ghmbl") {
          // Fetch GHMBL payments (payments made by GHMBL organization)
          response = await api.get("/api/payments/ghmbl");
        } else {
          // Fetch all player payments (default)
          response = await api.get("/api/payments");
        }
      }

      setPayments(response.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
      setError("Failed to load payments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle editing a payment
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setShowEditPayment(true);
    setOpenDropdown(null);
  };

  // Handle deleting a payment
  const handleDeletePayment = (payment) => {
    setDeletingPayment(payment);
    setShowDeleteConfirm(true);
    setOpenDropdown(null);
  };

  // Confirm delete payment
  const confirmDeletePayment = async () => {
    try {
      await api.delete(`/api/payments/${deletingPayment.id}`);
      setShowDeleteConfirm(false);
      setDeletingPayment(null);

      // Refresh payments and players
      fetchPayments();
      fetchPlayers(); // Refresh players to update balances

      // Refresh balance if user is a player
      if (userRole === "player") {
        fetchCurrentBalance();
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      setError("Failed to delete payment. Please try again.");
    }
  };

  // Toggle dropdown menu
  const toggleDropdown = (paymentId) => {
    setOpenDropdown(openDropdown === paymentId ? null : paymentId);
  };

  const fetchPlayers = async () => {
    try {
      const response = await api.get("/api/players");
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const getPlayerName = (playerId) => {
    if (playerId === "ghmbl") {
      return "HBC";
    }
    const player = players.find((p) => p.id === playerId);
    return player ? player.name : "Unknown Player";
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Sort payments
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case "date":
        aValue = new Date(a.date || 0).getTime();
        bValue = new Date(b.date || 0).getTime();
        break;
      case "player":
        aValue = getPlayerName(a.player_id).toLowerCase();
        bValue = getPlayerName(b.player_id).toLowerCase();
        break;
      case "amount":
        aValue = a.amount || 0;
        bValue = b.amount || 0;
        break;
      case "description":
        aValue = (a.description || "").toLowerCase();
        bValue = (b.description || "").toLowerCase();
        break;
      case "type":
        aValue = (a.payment_type || "").toLowerCase();
        bValue = (b.payment_type || "").toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Check if current user can add payments (Owner or Admin)
  const canAddPayments = userRole === "owner" || userRole === "admin";

  // Virtual scrolling calculations
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS
  );
  const endIndex = Math.min(
    sortedPayments.length,
    Math.ceil((scrollTop + ROW_HEIGHT * VISIBLE_ROWS) / ROW_HEIGHT) +
      BUFFER_ROWS
  );
  const visiblePayments = sortedPayments.slice(startIndex, endIndex);
  const totalHeight = sortedPayments.length * ROW_HEIGHT;
  const offsetY = startIndex * ROW_HEIGHT;

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading payments...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {userRole === "player" || userRole === "guest"
              ? "My Payments"
              : "Payments"}
          </h1>
          <p className="text-gray-600 mt-2">
            {userRole === "player" || userRole === "guest"
              ? "Your payment history and balance information"
              : "Track player payments and balance updates"}
          </p>
        </div>

        {userRole === "player" || userRole === "guest" ? (
          <div className="flex items-center space-x-4">
            {/* GHMBL Balance Display (Read-only for players/guests) */}
            <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                GHMBL Balance:
              </span>
              <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                ${ghmblBalance.toFixed(2)}
              </span>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl px-6 py-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                My Balance
              </p>
              <p
                className={`text-3xl font-bold ${
                  currentBalance < 0
                    ? "text-red-600 dark:text-red-400"
                    : "text-green-600 dark:text-green-400"
                }`}
              >
                ${Math.abs(currentBalance).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {currentBalance < 0 ? "Amount Owed" : "Credit Balance"}
              </p>
            </div>
            <a
              href="https://venmo.com/ghmblgroup?txn=pay&amount=100&note=Court-Fees"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              <div className="w-5 h-5 bg-white rounded flex items-center justify-center">
                <span className="text-blue-600 text-sm font-bold">V</span>
              </div>
              <span>Pay Now</span>
            </a>
          </div>
        ) : (
          <div className="flex items-center space-x-4">
            {/* GHMBL Balance Display/Edit (Editable for owner) */}
            <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                GHMBL Balance:
              </span>
              {userRole === "owner" && isEditingBalance ? (
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={tempBalance}
                    onChange={(e) => setTempBalance(e.target.value)}
                    className="w-28 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white dark:bg-gray-800"
                    placeholder="0.00"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveGhmblBalance}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded transition duration-200"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingBalance(false);
                      setTempBalance("");
                    }}
                    className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-sm font-semibold rounded transition duration-200"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                    ${ghmblBalance.toFixed(2)}
                  </span>
                  {userRole === "owner" && (
                    <button
                      onClick={() => {
                        setTempBalance(ghmblBalance.toString());
                        setIsEditingBalance(true);
                      }}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Edit GHMBL Balance"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* My Balance - Show for owner/admin too */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl px-6 py-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                My Balance
              </p>
              <p
                className={`text-3xl font-bold ${
                  currentBalance < 0
                    ? "text-red-600 dark:text-red-400"
                    : "text-green-600 dark:text-green-400"
                }`}
              >
                ${Math.abs(currentBalance).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {currentBalance < 0 ? "Amount Owed" : "Credit Balance"}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Filter:
              </label>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="personal">Personal</option>
                <option value="players">Players</option>
                <option value="ghmbl">GHMBL</option>
              </select>
            </div>
            {canAddPayments && (
              <button
                onClick={async () => {
                  // Ensure players are loaded before opening modal
                  if (players.length === 0) {
                    console.log("Players not loaded, fetching...");
                    await fetchPlayers();
                  }
                  setShowAddPayment(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
              >
                + Add Payment
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters Section - For Owner/Admin and Regular Players (not guests) */}
      {(userRole === "owner" ||
        userRole === "admin" ||
        (userRole === "player" && userType !== "guest")) && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Time Period Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time Period
              </label>
              <select
                value={selectedTimePeriodPayments}
                onChange={(e) => {
                  setSelectedTimePeriodPayments(e.target.value);
                  setShowCustomDatePayments(e.target.value === "custom");
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">All Time</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="thisWeek">This Week</option>
                <option value="lastWeek">Last Week</option>
                <option value="last6Months">Last 6 Months</option>
                <option value="thisYear">This Year</option>
                <option value="lastYear">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Range */}
            {showCustomDatePayments && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDatePayments}
                    onChange={(e) => setCustomStartDatePayments(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDatePayments}
                    onChange={(e) => setCustomEndDatePayments(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {/* Player Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Player
              </label>
              <select
                value={selectedPlayerPayments}
                onChange={(e) => setSelectedPlayerPayments(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">All Players</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {showAddPayment && (
        <AddPaymentModal
          players={players}
          paymentFilter={paymentFilter}
          onClose={() => setShowAddPayment(false)}
          onPaymentAdded={(addedPayment) => {
            setShowAddPayment(false);

            // Determine if we need to change the filter based on what was added
            if (addedPayment) {
              const isGhmblPayment = addedPayment.player_id === "ghmbl";

              // If we added a GHMBL payment but viewing players, or vice versa,
              // switch to the appropriate filter to show the newly added payment
              if (isGhmblPayment && paymentFilter === "players") {
                setPaymentFilter("ghmbl");
              } else if (!isGhmblPayment && paymentFilter === "ghmbl") {
                setPaymentFilter("players");
              }
            }

            // Show success message
            if (window.confirm) {
              // Use a more visible success indicator
              setTimeout(() => {
                alert(
                  "Payment added successfully! Player balance has been updated."
                );
              }, 100);
            }

            // Fetch will be triggered by filter change or we call it manually
            fetchPayments();
            fetchPlayers(); // Refresh players to update balances
          }}
        />
      )}

      {showEditPayment && editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          players={players}
          onClose={() => {
            setShowEditPayment(false);
            setEditingPayment(null);
          }}
          onPaymentUpdated={() => {
            setShowEditPayment(false);
            setEditingPayment(null);
            fetchPayments();
            fetchPlayers(); // Refresh players to update balances
          }}
        />
      )}

      {showDeleteConfirm && deletingPayment && (
        <DeletePaymentModal
          payment={deletingPayment}
          playerName={getPlayerName(deletingPayment.player_id)}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDeletingPayment(null);
          }}
          onConfirm={confirmDeletePayment}
        />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div
          className="overflow-x-auto overflow-y-auto"
          style={{
            position: "relative",
            maxHeight: `calc(56px + ${VISIBLE_ROWS} * ${ROW_HEIGHT}px)`,
          }}
          onScroll={handleScroll}
        >
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
              <tr>
                <th
                  className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort("date")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Date</span>
                    {sortField === "date" && (
                      <span className="text-xs">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
                {(userRole === "owner" || userRole === "admin") && (
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort("player")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Player</span>
                      {sortField === "player" && (
                        <span className="text-xs">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                )}
                <th
                  className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort("amount")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Amount</span>
                    {sortField === "amount" && (
                      <span className="text-xs">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort("description")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Description</span>
                    {sortField === "description" && (
                      <span className="text-xs">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort("type")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    {sortField === "type" && (
                      <span className="text-xs">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                  Notes
                </th>
                {(userRole === "owner" || userRole === "admin") && (
                  <th className="text-right py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {/* Spacer for rows above viewport */}
              {startIndex > 0 && (
                <tr style={{ height: `${offsetY}px` }}>
                  <td
                    colSpan="100%"
                    style={{ padding: 0, border: "none" }}
                  ></td>
                </tr>
              )}

              {/* Render only visible rows */}
              {visiblePayments.map((payment, relativeIndex) => {
                const index = startIndex + relativeIndex;
                return (
                  <tr
                    key={payment.id || index}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    style={{ height: `${ROW_HEIGHT}px` }}
                  >
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white">
                        {formatPaymentDate(payment.date)}
                      </p>
                    </td>
                    {(userRole === "owner" || userRole === "admin") && (
                      <td className="py-4 px-6">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPlayerName(payment.player_id)}
                        </p>
                      </td>
                    )}
                    <td className="py-4 px-6">
                      {payment.player_id === "ghmbl" ? (
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          -${payment.amount.toFixed(2)}
                        </span>
                      ) : (
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          +${payment.amount.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white">
                        {payment.description || "N/A"}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      {getPaymentTypeIcon(payment.payment_type)}
                    </td>
                    <td className="py-4 px-6">
                      {payment.notes ? (
                        <p
                          className="text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate"
                          title={payment.notes}
                        >
                          {payment.notes}
                        </p>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">
                          -
                        </span>
                      )}
                    </td>
                    {(userRole === "owner" || userRole === "admin") && (
                      <td className="py-4 px-6">
                        <div className="relative">
                          <button
                            onClick={() =>
                              toggleDropdown(
                                payment.id || startIndex + relativeIndex
                              )
                            }
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                            </svg>
                          </button>

                          {openDropdown ===
                            (payment.id || startIndex + relativeIndex) && (
                            <div className="absolute right-0 top-8 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[9999]">
                              <div className="py-1">
                                <button
                                  onClick={() => handleEditPayment(payment)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                  </svg>
                                  <span>Edit Payment</span>
                                </button>
                                <button
                                  onClick={() => handleDeletePayment(payment)}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                  </svg>
                                  <span>Remove Payment</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}

              {/* Spacer for rows below viewport */}
              {endIndex < sortedPayments.length && (
                <tr
                  style={{ height: `${totalHeight - endIndex * ROW_HEIGHT}px` }}
                >
                  <td
                    colSpan="100%"
                    style={{ padding: 0, border: "none" }}
                  ></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Footer */}
        {sortedPayments.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Showing {sortedPayments.length} payments
              </span>
              <div className="flex space-x-6">
                <span className="text-gray-600 dark:text-gray-400">
                  Total Amount:{" "}
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    $
                    {sortedPayments
                      .reduce((sum, payment) => sum + (payment.amount || 0), 0)
                      .toFixed(2)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {payments.length === 0 && !error && (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">💰</span>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No payments recorded
          </h3>
          <p className="text-gray-600 mb-4">
            Add your first payment to get started
          </p>
          {canAddPayments && (
            <button
              onClick={() => setShowAddPayment(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Add First Payment
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Add Payment Modal
const AddPaymentModal = ({
  players,
  paymentFilter,
  onClose,
  onPaymentAdded,
}) => {
  const [formData, setFormData] = useState({
    player_id: paymentFilter === "ghmbl" ? "ghmbl" : "",
    amount: "",
    payment_type: "venmo", // Default to Venmo
    payment_date: new Date().toISOString().split("T")[0], // Default to today
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const paymentData = {
        player_id: formData.player_id,
        amount: parseFloat(formData.amount),
        payment_type: formData.payment_type,
        date: formData.payment_date, // Use the selected date
        description: formData.notes || `${formData.payment_type} payment`, // Add required description field
      };
      if (formData.notes && formData.notes.trim()) {
        paymentData.notes = formData.notes;
      }
      const response = await api.post("/api/payments", paymentData);
      onPaymentAdded(paymentData); // Pass the payment data back
    } catch (error) {
      console.error("Error adding payment:", error);
      if (error.response?.status === 403) {
        setError("You do not have permission to add payments.");
      } else if (error.message) {
        setError(`Failed to add payment: ${error.message}`);
      } else {
        setError("Failed to add payment. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Payment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {paymentFilter !== "ghmbl" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Player <span className="text-red-500">*</span>
              </label>
              <select
                name="player_id"
                value={formData.player_id}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              >
                <option value="">
                  {players.length === 0
                    ? "Loading players..."
                    : "Select a player"}
                </option>
                <option value="ghmbl">HBC Charges</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name} (${(player.balance || 0).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              onInput={(e) => {
                // Additional validation for amount field
                let value = e.target.value;
                // Remove any non-numeric characters except decimal point
                value = value.replace(/[^0-9.]/g, "");
                // Ensure only one decimal point
                const parts = value.split(".");
                if (parts.length > 2) {
                  value = parts[0] + "." + parts.slice(1).join("");
                }
                // Limit to 2 decimal places
                if (parts[1] && parts[1].length > 2) {
                  value = parts[0] + "." + parts[1].substring(0, 2);
                }
                e.target.value = value;
              }}
              min="0"
              max="10000"
              step="0.01"
              pattern="[0-9]+(\.[0-9]{1,2})?"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter amount (e.g., 50.00)"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
              max={new Date().toISOString().split("T")[0]} // Prevent future dates
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Type <span className="text-red-500">*</span>
            </label>
            <select
              name="payment_type"
              value={formData.payment_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="manual">Manual</option>
              <option value="cash">Cash</option>
              <option value="venmo">Venmo</option>
              <option value="zelle">Zelle</option>
              <option value="check">Check</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder="Optional notes about this payment..."
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Optional field for additional payment details
            </p>
          </div>

          <div className="flex space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Payment Modal
const EditPaymentModal = ({ payment, players, onClose, onPaymentUpdated }) => {
  const [formData, setFormData] = useState({
    player_id: payment.player_id || "",
    amount: payment.amount || "",
    description: payment.description || "",
    payment_type: payment.payment_type || "manual",
    payment_date: payment.date
      ? new Date(payment.date).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    notes: payment.notes || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const paymentData = {
        player_id: formData.player_id,
        amount: parseFloat(formData.amount),
        description: formData.description,
        payment_type: formData.payment_type,
        date: formData.payment_date,
      };
      if (formData.notes && formData.notes.trim()) {
        paymentData.notes = formData.notes;
      }
      await api.put(`/api/payments/${payment.id}`, paymentData);
      onPaymentUpdated();
    } catch (error) {
      console.error("Error updating payment:", error);
      if (error.response?.status === 403) {
        setError("You do not have permission to edit payments.");
      } else if (error.message) {
        setError(`Failed to update payment: ${error.message}`);
      } else {
        setError("Failed to update payment. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Payment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Player <span className="text-red-500">*</span>
            </label>
            <select
              name="player_id"
              value={formData.player_id}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="">Select a player</option>
              <option value="ghmbl">HBC Charges</option>
              {players.map((player) => (
                <option key={player.id} value={player.id}>
                  {player.name} (${player.balance.toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              onInput={(e) => {
                // Additional validation for amount field
                let value = e.target.value;
                // Remove any non-numeric characters except decimal point
                value = value.replace(/[^0-9.]/g, "");
                // Ensure only one decimal point
                const parts = value.split(".");
                if (parts.length > 2) {
                  value = parts[0] + "." + parts.slice(1).join("");
                }
                // Limit to 2 decimal places
                if (parts[1] && parts[1].length > 2) {
                  value = parts[0] + "." + parts[1].substring(0, 2);
                }
                e.target.value = value;
              }}
              min="0"
              max="10000"
              step="0.01"
              pattern="[0-9]+(\.[0-9]{1,2})?"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
              max={new Date().toISOString().split("T")[0]} // Prevent future dates
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Type <span className="text-red-500">*</span>
            </label>
            <select
              name="payment_type"
              value={formData.payment_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="manual">Manual</option>
              <option value="venmo">Venmo</option>
              <option value="zelle">Zelle</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder="Optional notes about this payment..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 rounded-lg transition duration-200"
            >
              {loading ? "Updating..." : "Update Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Payment Modal
const DeletePaymentModal = ({ payment, playerName, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Confirm Delete
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="mb-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Delete Payment
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Player:
                </span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {playerName}
                </p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Amount:
                </span>
                <p className="font-medium text-green-600 dark:text-green-400">
                  ${payment.amount?.toFixed(2)}
                </p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Date:</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatPaymentDate(payment.date)}
                </p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Type:</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {payment.payment_type}
                </p>
              </div>
              {payment.description && (
                <div className="col-span-2">
                  <span className="text-gray-600 dark:text-gray-400">
                    Description:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {payment.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition duration-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg transition duration-200"
          >
            {loading ? "Deleting..." : "Delete Payment"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportsScreen = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState("player");
  const [userType, setUserType] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  // Filter states
  const [selectedFilter, setSelectedFilter] = useState("thisMonth");
  const [selectedPlayer, setSelectedPlayer] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDate, setShowCustomDate] = useState(false);
  // Removed activeTab state - consolidating all tabs into one view

  useEffect(() => {
    // Determine user role
    const determineUserRole = async () => {
      if (!user?.uid) return;

      try {
        // Check if user is the owner
        if (user?.email === "eltouch@gmail.com") {
          setUserRole("owner");
          setSelectedPlayer(user.uid); // Default to own reports
          return;
        }

        // For other users, fetch their role from the players collection
        const response = await api.get(`/api/players/${user.uid}`);
        const playerData = response.data;

        // Set user type
        if (playerData && playerData.user_type) {
          setUserType(playerData.user_type);
        }

        if (playerData && playerData.role) {
          setUserRole(playerData.role); // Could be 'admin' or 'player'

          // Default all users to their own reports
          // Admin, regular players, and guests all start with their own UID
          setSelectedPlayer(user.uid);
        } else {
          setUserRole("player"); // Default to player if no role specified
          setSelectedPlayer(user.uid);
        }
      } catch (error) {
        console.error("Error determining user role:", error);
        setUserRole("player");
      }
    };

    determineUserRole();
  }, [user]);

  useEffect(() => {
    if (user && userRole) {
      fetchReportData();
    }
  }, [userRole]);

  useEffect(() => {
    applyFilters();
  }, [
    sessions,
    payments,
    selectedFilter,
    selectedPlayer,
    customStartDate,
    customEndDate,
  ]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError("");

      // Don't fetch data if userRole is not set yet
      if (!userRole) {
        setLoading(false);
        return;
      }

      // Fetch sessions data
      const sessionsResponse = await api.get("/api/attendance");
      let allSessions = sessionsResponse.data || [];

      // Guests can only see their own sessions
      if (userType === "guest" && user?.uid) {
        allSessions = allSessions.filter(
          (session) =>
            session.players_present &&
            session.players_present.includes(user.uid)
        );
      }
      // Regular players can see all sessions (filtering happens via dropdown)

      setSessions(allSessions);

      // Fetch payments data
      let paymentsResponse;
      if (userType === "guest") {
        // Guests only see their own payments
        paymentsResponse = await api.get("/api/payments/personal");
      } else if (userRole === "player" && userType !== "guest") {
        // Regular players see all payments (filtering happens via dropdown)
        paymentsResponse = await api.get("/api/payments");
      } else {
        // Owner and admin see all payments
        paymentsResponse = await api.get("/api/payments");
      }
      setPayments(paymentsResponse.data || []);

      // Fetch players data for admin/owner and regular players (not guests)
      if (
        userRole === "owner" ||
        userRole === "admin" ||
        (userRole === "player" && userType !== "guest")
      ) {
        const playersResponse = await api.get("/api/players");
        setPlayers(playersResponse.data || []);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setError("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  // Sorting functions
  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400">⇅</span>;
    }
    return sortConfig.direction === "asc" ? (
      <span className="text-indigo-600">↑</span>
    ) : (
      <span className="text-indigo-600">↓</span>
    );
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === "date") {
        aValue = new Date(aValue || "").getTime() || 0;
        bValue = new Date(bValue || "").getTime() || 0;
      }
      // Handle numeric sorting for hours and cost
      else if (sortConfig.key === "hours" || sortConfig.key === "cost") {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }
      // Handle string sorting
      else {
        aValue = String(aValue || "").toLowerCase();
        bValue = String(bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Export CSV for Session Details only
  const exportSessionDetailsToCSV = () => {
    const sortedData = sortData(filteredData);
    const headers = ["Date", "Court Type", "Hours", "Players Present", "Cost"];
    const csvContent = [
      headers.join(","),
      ...sortedData.map((session) =>
        [
          formatDateCentral(session.date),
          session.court_type || "N/A",
          session.hours || 0,
          (session.players_present || []).length,
          `$${session.cost || 0}`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Session_Details_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print Session Details
  const printSessionDetails = () => {
    const sortedData = sortData(filteredData);
    const printWindow = window.open("", "_blank");
    const printContent = `
      <html>
        <head>
          <title>Session Details Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .cost { font-weight: bold; color: #16a34a; }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Session Details Report</h1>
          <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <p>Total Sessions: ${sortedData.length}</p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Court Type</th>
                <th>Hours</th>
                <th>Players Present</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              ${sortedData
                .map(
                  (session) => `
                <tr>
                  <td>${formatDateCentral(session.date)}</td>
                  <td>${session.court_type || "N/A"}</td>
                  <td>${session.hours || 0}</td>
                  <td>${(session.players_present || []).length}</td>
                  <td class="cost">$${session.cost || 0}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getDateRange = (filter) => {
    const now = new Date();
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    );

    switch (filter) {
      case "thisMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
        };
      case "lastMonth":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
        };
      case "thisWeek":
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return { start: startOfWeek, end: endOfDay };
      case "lastWeek":
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);
        return { start: lastWeekStart, end: lastWeekEnd };
      case "last6Months":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 6, 1),
          end: endOfDay,
        };
      case "thisYear":
        return {
          start: new Date(now.getFullYear(), 0, 1),
          end: endOfDay,
        };
      case "lastYear":
        return {
          start: new Date(now.getFullYear() - 1, 0, 1),
          end: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59),
        };
      case "custom":
        return {
          start: customStartDate
            ? new Date(customStartDate)
            : new Date(2020, 0, 1),
          end: customEndDate ? new Date(customEndDate + "T23:59:59") : endOfDay,
        };
      default:
        return { start: new Date(2020, 0, 1), end: endOfDay };
    }
  };

  const applyFilters = () => {
    let filteredSessions = [...sessions];

    // Date filter
    if (selectedFilter !== "all") {
      const { start, end } = getDateRange(selectedFilter);
      filteredSessions = filteredSessions.filter((session) => {
        // Parse date without timezone conversion
        const sessionDate = new Date(session.date + "T12:00:00");
        return sessionDate >= start && sessionDate <= end;
      });
    }

    // Player filter - available for Owner, Admin, and Regular Players (not guests)
    // Guests are already filtered at data fetch level
    if (
      (userRole === "owner" ||
        userRole === "admin" ||
        (userRole === "player" && userType !== "guest")) &&
      selectedPlayer !== "all"
    ) {
      filteredSessions = filteredSessions.filter(
        (session) =>
          session.players_present &&
          session.players_present.includes(selectedPlayer)
      );
    }

    setFilteredData(filteredSessions);
  };

  // Chart Data Generation
  const getCourtUsageByPlayerData = () => {
    if (
      userRole === "player" ||
      userRole === "guest" ||
      selectedPlayer !== "all"
    ) {
      // Single player view - show wood vs green usage
      const playerSessions =
        selectedPlayer !== "all"
          ? filteredData.filter((s) =>
              s.players_present?.includes(selectedPlayer)
            )
          : filteredData;

      const woodSessions = playerSessions.filter(
        (s) => s.court_type === "Wood"
      ).length;
      const greenSessions = playerSessions.filter(
        (s) => s.court_type === "Green"
      ).length;

      return {
        labels: ["Wood Court", "Green Court"],
        datasets: [
          {
            data: [woodSessions, greenSessions],
            backgroundColor: ["#eacb9d", "#059669"],
            hoverBackgroundColor: ["#d4b186", "#047857"],
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    } else {
      // All players view - show usage by player
      const playerUsage = {};
      players.forEach((player) => {
        const playerSessions = filteredData.filter((s) =>
          s.players_present?.includes(player.id)
        );
        // Extract first name only
        const firstName = player.name.split(" ")[0];
        playerUsage[firstName] = {
          wood: playerSessions.filter((s) => s.court_type === "Wood").length,
          green: playerSessions.filter((s) => s.court_type === "Green").length,
        };
      });

      return {
        labels: Object.keys(playerUsage),
        datasets: [
          {
            label: "Wood Court",
            data: Object.values(playerUsage).map((p) => p.wood),
            backgroundColor: "#eacb9d",
            borderColor: "#d4b186",
            borderWidth: 1,
          },
          {
            label: "Green Court",
            data: Object.values(playerUsage).map((p) => p.green),
            backgroundColor: "#059669",
            borderColor: "#047857",
            borderWidth: 1,
          },
        ],
      };
    }
  };

  const getMonthlyExpenseData = () => {
    // Get last 12 months data
    const monthlyExpenses = {};
    const now = new Date();

    // Initialize last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toISOString().slice(0, 7); // YYYY-MM format
      monthlyExpenses[monthKey] = 0;
    }

    // Calculate expenses from sessions
    sessions.forEach((session) => {
      // Parse date without timezone conversion
      const sessionDate = new Date(session.date + "T12:00:00");
      const monthKey = sessionDate.toISOString().slice(0, 7);

      if (monthlyExpenses.hasOwnProperty(monthKey)) {
        if (selectedPlayer !== "all") {
          // For specific player, only count their portion
          if (session.players_present?.includes(selectedPlayer)) {
            monthlyExpenses[monthKey] += session.cost_per_person || 0;
          }
        } else {
          // For all players, count total session cost
          monthlyExpenses[monthKey] += session.total_cost || 0;
        }
      }
    });

    return {
      labels: Object.keys(monthlyExpenses).map((month) => {
        const date = new Date(month + "-01");
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
      }),
      datasets: [
        {
          label:
            selectedPlayer !== "all" ? "Personal Expenses" : "Total Expenses",
          data: Object.values(monthlyExpenses),
          backgroundColor: "rgba(99, 102, 241, 0.1)",
          borderColor: "rgb(99, 102, 241)",
          borderWidth: 2,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  const exportToCSV = () => {
    if (!filteredData.length) return;

    const csvData = [];

    if (userRole === "owner" || userRole === "admin") {
      csvData.push([
        "Date",
        "Court Type",
        "Hours",
        "Players",
        "Total Cost",
        "Notes",
      ]);

      filteredData.forEach((session) => {
        csvData.push([
          new Date(session.date + "T12:00:00").toLocaleDateString(),
          session.court_type || "",
          session.hours || 0,
          (session.players_present?.length || 0) +
            (session.guests_present?.length || 0),
          `$${(session.total_cost || 0).toFixed(2)}`,
          session.notes || "",
        ]);
      });
    } else {
      csvData.push(["Date", "Court Type", "Hours", "My Cost", "Balance After"]);

      filteredData.forEach((session) => {
        csvData.push([
          new Date(session.date + "T12:00:00").toLocaleDateString(),
          session.court_type || "",
          session.hours || 0,
          `$${(session.cost_per_person || 0).toFixed(2)}`,
          session.balance_after || "N/A",
        ]);
      });
    }

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const playerName =
      selectedPlayer !== "all"
        ? players.find((p) => p.id === selectedPlayer)?.name || "Player"
        : "All";

    a.download = `${playerName}_${getFilterTitle()}_Report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getFilterTitle = () => {
    switch (selectedFilter) {
      case "thisMonth":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      case "thisWeek":
        return "This Week";
      case "lastWeek":
        return "Last Week";
      case "last6Months":
        return "Last 6 Months";
      case "thisYear":
        return "This Year";
      case "lastYear":
        return "Last Year";
      case "custom":
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  const getPageTitle = () => {
    if (selectedPlayer !== "all") {
      const player = players.find((p) => p.id === selectedPlayer);
      return `${player?.name || "Player"}'s Report`;
    }

    // Dynamic title based on filter
    switch (selectedFilter) {
      case "lastYear":
        return "Last Year Report";
      case "thisYear":
        return "This Year Report";
      case "last6Months":
        return "Last 6 Months Report";
      case "lastMonth":
        return "Last Month Report";
      case "thisMonth":
        return "Monthly Report";
      case "lastWeek":
        return "Last Week Report";
      case "thisWeek":
        return "This Week Report";
      case "custom":
        return "Custom Range Report";
      default:
        return "Monthly Report";
    }
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalSessions = filteredData.length;
    const totalHours = filteredData.reduce(
      (sum, session) => sum + (session.hours || 0),
      0
    );
    const totalCost = filteredData.reduce((sum, session) => {
      if (selectedPlayer !== "all") {
        return sum + (session.cost_per_person || 0);
      }
      return sum + (session.total_cost || 0);
    }, 0);

    const woodSessions = filteredData.filter(
      (s) => s.court_type === "Wood"
    ).length;
    const greenSessions = filteredData.filter(
      (s) => s.court_type === "Green"
    ).length;

    return {
      totalSessions,
      totalHours: totalHours.toFixed(1),
      totalCost: totalCost.toFixed(2),
      woodSessions,
      greenSessions,
      avgCostPerSession:
        totalSessions > 0 ? (totalCost / totalSessions).toFixed(2) : "0.00",
    };
  }, [filteredData, selectedPlayer]);

  // Fetch data on component mount and when user changes
  useEffect(() => {
    if (user) {
      fetchReportData();
    }
  }, [user, userRole]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFilters();
  }, [
    sessions,
    selectedFilter,
    selectedPlayer,
    customStartDate,
    customEndDate,
    userRole,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          Loading reports...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {getPageTitle()}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {getFilterTitle()} • {filteredData.length} sessions • $
            {summaryStats.totalCost} total
          </p>
        </div>

        {filteredData.length > 0 && (
          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition duration-200"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 10v6m0-6L9 7m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
            </svg>
            <span>Export CSV</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Period
            </label>
            <select
              value={selectedFilter}
              onChange={(e) => {
                setSelectedFilter(e.target.value);
                setShowCustomDate(e.target.value === "custom");
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="thisWeek">This Week</option>
              <option value="lastWeek">Last Week</option>
              <option value="last6Months">Last 6 Months</option>
              <option value="thisYear">This Year Till Date</option>
              <option value="lastYear">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {showCustomDate && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </>
          )}

          {/* Player Filter - For Owner/Admin and Regular Players (not guests) */}
          {(userRole === "owner" ||
            userRole === "admin" ||
            (userRole === "player" && userType !== "guest")) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Player
              </label>
              <select
                value={selectedPlayer}
                onChange={(e) => setSelectedPlayer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">All Players</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name.split(" ")[0]}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Unified Reports View - Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Sessions
              </p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {summaryStats.totalSessions}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🏸</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Hours
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {summaryStats.totalHours}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">⏱️</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Cost
              </p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                ${summaryStats.totalCost}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Avg/Session
              </p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                ${summaryStats.avgCostPerSession}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📊</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="space-y-6 mb-8">
        {/* Court Usage Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedPlayer !== "all" || userRole === "player"
              ? "Court Type Preference"
              : "Court Usage by Player"}
          </h3>
          <div className="h-80">
            {selectedPlayer !== "all" || userRole === "player" ? (
              <Pie
                data={getCourtUsageByPlayerData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: {
                        padding: 20,
                        usePointStyle: true,
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const total = context.dataset.data.reduce(
                            (a, b) => a + b,
                            0
                          );
                          const percentage = (
                            (context.parsed / total) *
                            100
                          ).toFixed(1);
                          return `${context.label}: ${context.parsed} sessions (${percentage}%)`;
                        },
                      },
                    },
                  },
                }}
              />
            ) : (
              <Bar
                data={getCourtUsageByPlayerData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1,
                      },
                    },
                  },
                }}
              />
            )}
          </div>
        </div>

        {/* Monthly Expense Trend */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Monthly Expense Trend (Last 12 Months)
          </h3>
          <div className="h-80">
            <Bar
              data={getMonthlyExpenseData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `${
                          context.dataset.label
                        }: $${context.parsed.y.toFixed(2)}`;
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function (value) {
                        return "$" + value.toFixed(0);
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Detailed Table Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              📋 Session Details
            </h3>

            <div className="flex items-center space-x-2">
              {/* Export CSV - Icon Only */}
              <button
                onClick={exportSessionDetailsToCSV}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Export CSV"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </button>

              {/* Print - Icon Only */}
              <button
                onClick={printSessionDetails}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Print"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
        {filteredData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("date")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date</span>
                      <span className="text-gray-400">
                        {getSortIcon("date")}
                      </span>
                    </div>
                  </th>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("court_type")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Court</span>
                      <span className="text-gray-400">
                        {getSortIcon("court_type")}
                      </span>
                    </div>
                  </th>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("hours")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Hours</span>
                      <span className="text-gray-400">
                        {getSortIcon("hours")}
                      </span>
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Players
                  </th>
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("cost")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>
                        {selectedPlayer !== "all" ? "My Cost" : "Total Cost"}
                      </span>
                      <span className="text-gray-400">
                        {getSortIcon("cost")}
                      </span>
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">
                    Cost/Person
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortData(filteredData).map((session, index) => (
                  <tr
                    key={session.id || index}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {formatDateCentral(session.date)}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          session.court_type === "Wood"
                            ? "bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300"
                            : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                        }`}
                      >
                        {session.court_type}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white">
                        {session.hours}h
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white">
                        {(session.players_present?.length || 0) +
                          (session.guests_present?.length || 0)}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        $
                        {selectedPlayer !== "all"
                          ? (session.cost_per_person || 0).toFixed(2)
                          : (session.total_cost || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-700 dark:text-gray-300">
                        ${(session.cost_per_person || 0).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <span className="text-6xl mb-4 block">📊</span>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No data found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your filters to see report data
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const AccountScreen = () => {
  const { user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [playerName, setPlayerName] = useState("");
  const [userType, setUserType] = useState("");
  const [loading, setLoading] = useState(true);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Data Protection states
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupError, setBackupError] = useState("");
  const [backupSuccess, setBackupSuccess] = useState("");
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);

  const isOwner = user?.email === "eltouch@gmail.com";

  useEffect(() => {
    fetchPlayerName();
    if (isOwner) {
      fetchBackups();
    }
  }, [user, isOwner]);

  const fetchBackups = async () => {
    try {
      setBackupLoading(true);
      setBackupError("");
      const response = await api.get("/api/database/backups");
      setBackups(response.data || []);
    } catch (error) {
      console.error("Error fetching backups:", error);
      setBackupError("Failed to load backups list.");
    } finally {
      setBackupLoading(false);
    }
  };

  const handleBackupDatabase = async () => {
    if (
      !window.confirm(
        "Are you sure you want to create a database backup?\n\nThis will save the current state of all data."
      )
    ) {
      return;
    }

    try {
      setBackupInProgress(true);
      setBackupError("");
      setBackupSuccess("");

      const response = await api.post("/api/database/backup");

      setBackupSuccess(
        `✅ Database backup created successfully! (${response.data.filename})`
      );

      // Refresh backups list
      await fetchBackups();
    } catch (error) {
      console.error("Error creating backup:", error);
      setBackupError(
        error.response?.data?.detail ||
          "Failed to create database backup. Please try again."
      );
    } finally {
      setBackupInProgress(false);
    }
  };

  const handleRestoreDatabase = async (backupFilename, backupDate) => {
    const confirmMessage =
      "⚠️ WARNING: This will ERASE ALL current data and restore the database to:\n\n" +
      backupDate +
      "\n\nThis action CANNOT be undone!\n\nType 'RESTORE' to confirm:";
    const userInput = window.prompt(confirmMessage);

    if (userInput !== "RESTORE") {
      return;
    }

    try {
      setRestoreInProgress(true);
      setBackupError("");
      setBackupSuccess("");

      await api.post("/api/database/restore", {
        filename: backupFilename,
      });

      setBackupSuccess(
        "✅ Database restored successfully! Please refresh the page."
      );

      // Refresh backups list
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error("Error restoring database:", error);
      setBackupError(
        error.response?.data?.detail ||
          "Failed to restore database. Please try again."
      );
    } finally {
      setRestoreInProgress(false);
    }
  };

  const handleDeleteBackup = async (backupFilename, backupDate, event) => {
    event.stopPropagation();

    const formattedDate = formatBackupTimestamp(backupDate);

    if (
      !window.confirm(
        `Are you sure you want to delete this backup?\n\nFile: ${backupFilename}\nDate: ${formattedDate}\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setBackupError("");
      await api.delete(`/api/database/backups/${backupFilename}`);
      setBackupSuccess(`Backup deleted successfully.`);

      // Refresh backups list
      await fetchBackups();
    } catch (error) {
      console.error("Error deleting backup:", error);
      setBackupError("Failed to delete backup.");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) {
      return `${mb.toFixed(2)} MB`;
    }
    return `${kb.toFixed(2)} KB`;
  };

  const formatBackupTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";

    const date = new Date(timestamp);

    // Format: Nov 9, 2025 | 6:09:36 PM
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    return `${formattedDate} | ${formattedTime}`;
  };

  const handleDownloadExcel = async (filename) => {
    try {
      const response = await api.get(
        `/api/database/backups/${filename}/download`,
        {
          responseType: "blob",
        }
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      setBackupError("Failed to download Excel file.");
    }
  };

  const fetchPlayerName = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/api/players");
      const players = response.data || [];
      const currentPlayer = players.find((p) => p.id === user.uid);

      if (currentPlayer) {
        setPlayerName(currentPlayer.name);
        setUserType(currentPlayer.user_type || "");
      } else {
        // Special case for owner account
        if (user.email === "eltouch@gmail.com") {
          setPlayerName("Ben Joy");
          setUserType("regular");
        } else {
          // Fallback to email if player not found
          setPlayerName(user.email.split("@")[0]);
          setUserType("regular");
        }
      }
    } catch (error) {
      console.error("Error fetching player name:", error);
      // Special case for owner account
      if (user.email === "eltouch@gmail.com") {
        setPlayerName("Ben Joy");
        setUserType("regular");
      } else {
        // Fallback to email if API fails
        setPlayerName(user.email.split("@")[0]);
        setUserType("regular");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return;
    }

    try {
      setLoading(true);

      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        setPasswordError("No user is currently logged in");
        return;
      }

      // Re-authenticate the user before changing password
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );

      await reauthenticateWithCredential(currentUser, credential);

      // Update the password
      await updatePassword(currentUser, passwordData.newPassword);

      setPasswordSuccess("Password successfully updated!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setIsChangingPassword(false);
    } catch (error) {
      console.error("Password change error:", error);
      if (error.code === "auth/wrong-password") {
        setPasswordError("Current password is incorrect");
      } else if (error.code === "auth/weak-password") {
        setPasswordError("New password is too weak");
      } else if (error.code === "auth/requires-recent-login") {
        setPasswordError("Please log out and log back in, then try again");
      } else {
        setPasswordError("Failed to change password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      // Send password reset email
      await sendPasswordResetEmail(auth, user.email);
      setPasswordSuccess("Password reset email sent to your email address.");
    } catch (error) {
      console.error("Password reset error:", error);
      setPasswordError(
        "Failed to send password reset email. Please try again."
      );
    }
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Manage your account and preferences
          </p>
        </div>

        {/* Account Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Account Information
            </h2>
          </div>
          <div className="p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
                <span className="text-2xl font-semibold text-indigo-600 dark:text-indigo-400">
                  {loading
                    ? "..."
                    : playerName?.charAt(0).toUpperCase() ||
                      user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {loading ? "Loading..." : playerName || user?.email}
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {user?.email === "eltouch@gmail.com"
                    ? "👑 Owner"
                    : "League Member"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <p className="text-gray-900 dark:text-white">
                  {loading ? "Loading..." : playerName || "Not available"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <p className="text-gray-900 dark:text-white">{user?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  User ID
                </label>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-mono">
                  {user?.uid}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    user?.email === "eltouch@gmail.com"
                      ? "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
                  }`}
                >
                  {user?.email === "eltouch@gmail.com"
                    ? "👑 Owner"
                    : "🏸 Player"}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Sign In
                </label>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {user?.metadata?.lastSignInTime
                    ? new Date(user.metadata.lastSignInTime).toLocaleString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Password Management - Hidden for guests */}
        {userType !== "guest" && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Security
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Manage your password and account security
              </p>
            </div>
            <div className="p-6">
              {passwordSuccess && (
                <div className="mb-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">
                  {passwordSuccess}
                </div>
              )}

              {passwordError && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                  {passwordError}
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🔐</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Password
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Last changed:{" "}
                        {user?.metadata?.lastPasswordUpdateTime || "Never"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsChangingPassword(!isChangingPassword)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition duration-200"
                  >
                    {isChangingPassword ? "Cancel" : "Change Password"}
                  </button>
                </div>

                {/* Password Change Form */}
                {isChangingPassword && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              currentPassword: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="Enter your current password"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              newPassword: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="Enter new password (min 6 characters)"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              confirmPassword: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="Confirm your new password"
                          required
                        />
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
                        >
                          {loading ? "Updating..." : "Update Password"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🛡️</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Account Created
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {user?.metadata?.creationTime
                          ? new Date(
                              user.metadata.creationTime
                            ).toLocaleDateString()
                          : "Unknown"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Theme Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Appearance
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Customize how GHMBL looks on your device
            </p>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{isDarkMode ? "🌙" : "☀️"}</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {isDarkMode ? "Dark Mode" : "Light Mode"}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {isDarkMode
                      ? "Switch to light theme"
                      : "Switch to dark theme"}
                  </p>
                </div>
              </div>
              <button
                onClick={toggleTheme}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                  isDarkMode ? "bg-indigo-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isDarkMode ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Data Protection - Owner Only */}
        {isOwner && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                🔒 Data Protection
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Database backup and restore management
              </p>
            </div>
            <div className="p-6">
              {/* Alerts */}
              {backupError && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                  {backupError}
                </div>
              )}

              {backupSuccess && (
                <div className="mb-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">
                  {backupSuccess}
                </div>
              )}

              {/* Create Backup Section */}
              <div className="mb-6 p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">💾</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Create Backup
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Save current database state (JSON & Excel)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleBackupDatabase}
                    disabled={backupInProgress || restoreInProgress}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200"
                  >
                    {backupInProgress ? "Creating..." : "💾 Backup Now"}
                  </button>
                </div>
              </div>

              {/* Restore Backup Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Available Backups
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Click to download or restore from previous backups
                  </p>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    <strong>⚠️ Warning:</strong> Restoring will erase all
                    current data. Create a backup first!
                  </p>
                </div>

                {backupLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Loading backups...
                    </p>
                  </div>
                ) : backups.length === 0 ? (
                  <div className="text-center py-8 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <span className="text-4xl mb-2 block">📦</span>
                    <p className="text-gray-600 dark:text-gray-400">
                      No backups available
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {backups.map((backup, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <span className="text-2xl">💾</span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {backup.filename}
                            </p>
                            <div className="flex items-center space-x-3 text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <span>
                                📅{" "}
                                {formatBackupTimestamp(
                                  backup.date || backup.created_at
                                )}
                              </span>
                              {backup.size && (
                                <span>📊 {formatFileSize(backup.size)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          {backup.excel_filename && (
                            <button
                              onClick={() =>
                                handleDownloadExcel(backup.excel_filename)
                              }
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded transition duration-200"
                            >
                              📊 Export
                            </button>
                          )}
                          <button
                            onClick={() =>
                              handleRestoreDatabase(
                                backup.filename,
                                formatBackupTimestamp(
                                  backup.date || backup.created_at
                                )
                              )
                            }
                            disabled={restoreInProgress || backupInProgress}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded transition duration-200"
                          >
                            ♻️ Restore
                          </button>
                          <button
                            onClick={(e) =>
                              handleDeleteBackup(
                                backup.filename,
                                backup.date || backup.created_at,
                                e
                              )
                            }
                            disabled={restoreInProgress || backupInProgress}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded transition duration-200"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* App Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              About GHMBL
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Application
                </label>
                <p className="text-gray-900 dark:text-white">
                  Greater Houston Malayalee Badminton League
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Version
                </label>
                <p className="text-gray-900 dark:text-white">v1.0.0</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Features
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-full">
                    Player Management
                  </span>
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 rounded-full">
                    Attendance Tracking
                  </span>
                  <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300 rounded-full">
                    Payment System
                  </span>
                  <span className="px-2 py-1 text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-300 rounded-full">
                    Monthly Reports
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Support
                </label>
                <p className="text-gray-900 dark:text-white">
                  Contact league administrator
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mindbody Integration Screen
const MindbodyScreen = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lastFetched, setLastFetched] = useState(null);
  const [showCustomizeColumns, setShowCustomizeColumns] = useState(false);
  const [userRole, setUserRole] = useState("player");
  const [sortConfig, setSortConfig] = useState({
    key: "saleDate",
    direction: "desc",
  });
  const [selectedFilter, setSelectedFilter] = useState("thisMonth");
  const [selectedPaymentType, setSelectedPaymentType] = useState("all"); // New filter for payment types
  const [customDateRange, setCustomDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  // Column configuration
  const [visibleColumns, setVisibleColumns] = useState({
    saleDate: true, // Required - always visible
    description: true,
    amountPaid: true, // Required - always visible
    price: true,
    tax: true,
    // Additional columns will be added dynamically
  });

  const availableColumns = [
    { key: "saleDate", label: "Sale Date", required: true },
    { key: "description", label: "Description", required: false },
    { key: "amountPaid", label: "Amount Paid", required: true },
    { key: "price", label: "Price", required: false },
    { key: "tax", label: "Tax", required: false },
  ];

  // Determine user role
  useEffect(() => {
    const determineUserRole = async () => {
      if (!user?.uid) return;

      try {
        // Check if user is the owner
        if (user?.email === "eltouch@gmail.com") {
          setUserRole("owner");
          return;
        }

        // For other users, fetch their role from the players collection
        const response = await api.get(`/api/players/${user.uid}`);
        const playerData = response.data;

        if (playerData && playerData.role) {
          setUserRole(playerData.role); // Could be 'admin', 'player', or 'guest'
        } else {
          setUserRole("player"); // Default to player if no role specified
        }
      } catch (error) {
        console.error("Error determining user role:", error);
        setUserRole("player");
      }
    };

    determineUserRole();
  }, [user]);

  // Check if user can access HBC Charges (all except guests)
  const canAccessMindbody = () => {
    return (
      userRole === "owner" || userRole === "admin" || userRole === "player"
    );
  };

  // Format currency values - make GHMBL Payments negative
  const formatCurrency = (value, description = "") => {
    if (!value) return "N/A";
    const numValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[$,]/g, ""))
        : value;
    if (isNaN(numValue)) return value;

    // Make GHMBL Payments negative (they are payments made by GHMBL to HBC)
    const isGHMBLPayment = shouldHighlightRow(description);
    const finalValue = isGHMBLPayment ? -Math.abs(numValue) : numValue;

    return `$${finalValue.toFixed(2)}`;
  };

  // Format date to MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format description
  const formatDescription = (description) => {
    if (!description) return "N/A";
    return description
      .replace(/Two-Hour Court Rental/gi, "2 hr")
      .replace(/1-Hr Ct Rental/gi, "1 hr")
      .replace(/Account Payment/gi, "GHMBL Payment");
  };

  // Check if row should be highlighted (GHMBL Payment)
  const shouldHighlightRow = (description) => {
    return (
      description &&
      (description.toLowerCase().includes("ghmbl payment") ||
        description.toLowerCase().includes("account payment"))
    );
  };

  // Filter data by payment type
  const filterDataByPaymentType = (data) => {
    if (selectedPaymentType === "all") return data;

    if (selectedPaymentType === "hbc") {
      // HBC charges are everything except GHMBL payments
      return data.filter((item) => !shouldHighlightRow(item.description));
    } else if (selectedPaymentType === "ghmbl") {
      // GHMBL payments only
      return data.filter((item) => shouldHighlightRow(item.description));
    }

    return data;
  };

  // Calculate total charges from filtered data
  const calculateTotalCharges = (data) => {
    if (!Array.isArray(data) || data.length === 0) return 0;

    return data.reduce((total, item) => {
      // Get the amount charged value
      let amount = 0;

      // Handle different possible field names for amount
      const amountValue =
        item.amountPaid ||
        item["Amount Paid"] ||
        item.amount ||
        item["Amount Charged"] ||
        "0";

      // Remove currency symbols and convert to number
      const numericAmount = parseFloat(
        String(amountValue).replace(/[$,]/g, "")
      );

      if (!isNaN(numericAmount)) {
        amount = numericAmount;
      }

      return total + Math.abs(amount); // Use absolute value for total calculation
    }, 0);
  };

  // Get enhanced transaction history title with session count and total charges
  const getTransactionHistoryTitle = () => {
    const totalCharges = calculateTotalCharges(filteredData);
    const recordCount = filteredData.length;

    // Format total charges as currency
    const formattedTotal = `$${totalCharges.toFixed(2)}`;

    const now = new Date();
    let baseTitle = "";

    switch (selectedFilter) {
      case "thisMonth":
        baseTitle = now.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
        break;
      case "lastMonth":
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
        baseTitle = lastMonth.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
        break;
      case "thisWeek":
        baseTitle = "This Week";
        break;
      case "lastWeek":
        baseTitle = "Last Week";
        break;
      case "last6Months":
        baseTitle = "Last 6 Months";
        break;
      case "thisYear":
        baseTitle = `${now.getFullYear()}`;
        break;
      case "lastYear":
        baseTitle = `${now.getFullYear() - 1}`;
        break;
      case "custom":
        baseTitle = "Custom Range";
        break;
      default:
        baseTitle = "All Time";
    }

    // Use "sessions" instead of "records" and format as requested
    return `${baseTitle} (${recordCount} sessions, Total Charges: ${formattedTotal})`;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Date", "Description", "Amount Charged", "Price", "Tax"];
    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          formatDate(row.saleDate),
          `"${formatDescription(row.description)}"`,
          formatCurrency(row.amountPaid, row.description),
          formatCurrency(row.price, row.description),
          formatCurrency(row.tax, row.description),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `HBC_Charges_${getTransactionHistoryTitle().replace(/\s+/g, "_")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const printContent = `
      <html>
        <head>
          <title>HBC Charges - ${getTransactionHistoryTitle()}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .highlight { background-color: #fff3cd; }
            .amount { font-weight: bold; color: #16a34a; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>HBC Charges - ${getTransactionHistoryTitle()}</h1>
          <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <p>Total Records: ${filteredData.length}</p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount Charged</th>
                <th>Price</th>
                <th>Tax</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData
                .map(
                  (row) => `
                <tr class="${
                  shouldHighlightRow(row.description) ? "highlight" : ""
                }">
                  <td>${formatDate(row.saleDate)}</td>
                  <td>${formatDescription(row.description)}</td>
                  <td class="amount">${formatCurrency(
                    row.amountPaid,
                    row.description
                  )}</td>
                  <td class="amount">${formatCurrency(
                    row.price,
                    row.description
                  )}</td>
                  <td class="amount">${formatCurrency(
                    row.tax,
                    row.description
                  )}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Get filter title
  const getFilterTitle = () => {
    const now = new Date();
    switch (selectedFilter) {
      case "thisMonth":
        return `Mindbody data for ${now.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })}`;
      case "lastMonth":
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
        return `Mindbody data for ${lastMonth.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })}`;
      case "thisWeek":
        return "Mindbody data for This Week";
      case "lastWeek":
        return "Mindbody data for Last Week";
      case "last6Months":
        return "Mindbody data for Last 6 Months";
      case "thisYear":
        return `Mindbody data for ${now.getFullYear()}`;
      case "lastYear":
        return `Mindbody data for ${now.getFullYear() - 1}`;
      case "custom":
        return "Mindbody data for Custom Range";
      default:
        return "Mindbody data for All Time";
    }
  };

  // Filter data by time period
  const filterDataByTimePeriod = (rawData) => {
    if (!Array.isArray(rawData)) return [];

    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    return rawData.filter((item) => {
      const itemDate = new Date(
        item.saleDate || item["Sale Date"] || item.date || ""
      );
      if (isNaN(itemDate.getTime())) return true; // Include items with invalid dates

      switch (selectedFilter) {
        case "thisMonth":
          return (
            itemDate.getMonth() === now.getMonth() &&
            itemDate.getFullYear() === now.getFullYear()
          );

        case "lastMonth":
          const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
          const lastMonthYear =
            now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
          return (
            itemDate.getMonth() === lastMonth &&
            itemDate.getFullYear() === lastMonthYear
          );

        case "thisWeek":
          const startOfWeek = new Date(startOfToday);
          startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
          return itemDate >= startOfWeek && itemDate <= now;

        case "lastWeek":
          const startOfLastWeek = new Date(startOfToday);
          startOfLastWeek.setDate(
            startOfToday.getDate() - startOfToday.getDay() - 7
          );
          const endOfLastWeek = new Date(startOfLastWeek);
          endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
          return itemDate >= startOfLastWeek && itemDate <= endOfLastWeek;

        case "last6Months":
          const sixMonthsAgo = new Date(
            now.getFullYear(),
            now.getMonth() - 6,
            now.getDate()
          );
          return itemDate >= sixMonthsAgo && itemDate <= now;

        case "thisYear":
          return itemDate.getFullYear() === now.getFullYear();

        case "lastYear":
          return itemDate.getFullYear() === now.getFullYear() - 1;

        case "custom":
          if (customDateRange.startDate && customDateRange.endDate) {
            const startDate = new Date(customDateRange.startDate);
            const endDate = new Date(customDateRange.endDate);
            return itemDate >= startDate && itemDate <= endDate;
          }
          return true;

        default:
          return true;
      }
    });
  };

  // Sort data
  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (
        sortConfig.key === "saleDate" ||
        sortConfig.key.toLowerCase().includes("date")
      ) {
        aValue = new Date(aValue || "").getTime() || 0;
        bValue = new Date(bValue || "").getTime() || 0;
      }
      // Handle currency sorting
      else if (
        sortConfig.key.toLowerCase().includes("amount") ||
        sortConfig.key.toLowerCase().includes("price") ||
        sortConfig.key.toLowerCase().includes("tax")
      ) {
        aValue = parseFloat(String(aValue || "0").replace(/[$,]/g, "")) || 0;
        bValue = parseFloat(String(bValue || "0").replace(/[$,]/g, "")) || 0;
      }
      // Handle string sorting
      else {
        aValue = String(aValue || "").toLowerCase();
        bValue = String(bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400">⇅</span>;
    }
    return sortConfig.direction === "asc" ? (
      <span className="text-indigo-600">↑</span>
    ) : (
      <span className="text-indigo-600">↓</span>
    );
  };

  const toggleColumnVisibility = (columnKey) => {
    if (columnKey === "saleDate" || columnKey === "amountPaid") return; // Required columns

    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const fetchTransactions = async () => {
    // Check if anyone has fetched today (global check)
    const today = new Date().toDateString();
    const cachedData = localStorage.getItem("hbc_transactions_cache");
    const cacheDate = localStorage.getItem("hbc_transactions_cache_date");
    const cacheUser = localStorage.getItem("hbc_transactions_cache_user");

    if (cachedData && cacheDate === today) {
      // Use cached data
      const parsedData = JSON.parse(cachedData);
      const cacheTime = new Date(
        localStorage.getItem("hbc_transactions_cache_time")
      );

      setData(parsedData);
      setLastFetched(cacheTime);
      setSuccess(
        `✅ Synced ${parsedData.length} transaction records (cached data from ${cacheUser})`
      );

      // Update visible columns
      if (parsedData.length > 0) {
        const newVisibleColumns = { ...visibleColumns };
        Object.keys(parsedData[0]).forEach((key) => {
          if (!(key in newVisibleColumns)) {
            newVisibleColumns[key] = true;
          }
        });
        setVisibleColumns(newVisibleColumns);
      }
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      console.log("Fetching data from Google Script...");

      const response = await fetch(
        "https://script.googleusercontent.com/macros/echo?user_content_key=AehSKLgdVMnSNUw8IVYdGxjspbh40pQ4y7O_UJoBM5j2dis3g5upSN2aNm1Sa4a4aUOAq_ki5ajLYY3GLdv-JtgGi-zAW-xOnTPQXiGVqRcB-7osuronnoMWAhyw5q1_8htIuNvhfNe3A-dJ7JiSUZmkoQpyJCzFMvQeu1IidrhvQ3WgyUsJx3dy_bzRvqp8pcdT6eKfc1zA44DqPcIjhG-OlzzyDJidMfw4b0eILFRCV2h-z5HZT7qDvuAq5OchZWPM0MK1GHcaeJH00CxhJcwFxSBAlIFfSK_dCXc7vp-sqfuinVSRW5Zh1oWAD_ZVSZDsRun3mzjl&lib=M6VAP7FECFD7lfcgODQkRL-NCrjc9euki"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fetchedData = await response.json();
      console.log("Fetched data:", fetchedData);

      // Handle different data structures and normalize column names
      let processedData = [];
      if (Array.isArray(fetchedData)) {
        processedData = fetchedData.map((item) => {
          // Only keep specific normalized columns - no additional fields
          const normalizedItem = {
            saleDate: item["Sale Date"] || item.saleDate || item.date,
            description: item["Description"] || item.description || item.item,
            amountPaid: item["Amount Paid"] || item.amountPaid || item.amount,
            price: item["Price"] || item.price,
            tax: item["Tax"] || item.tax,
          };

          return normalizedItem;
        });
      } else if (fetchedData.data && Array.isArray(fetchedData.data)) {
        processedData = fetchedData.data.map((item) => {
          const normalizedItem = {
            saleDate: item["Sale Date"] || item.saleDate || item.date,
            description: item["Description"] || item.description || item.item,
            amountPaid: item["Amount Paid"] || item.amountPaid || item.amount,
            price: item["Price"] || item.price,
            tax: item["Tax"] || item.tax,
          };

          return normalizedItem;
        });
      } else if (typeof fetchedData === "object") {
        const normalizedItem = {
          saleDate:
            fetchedData["Sale Date"] ||
            fetchedData.saleDate ||
            fetchedData.date,
          description:
            fetchedData["Description"] ||
            fetchedData.description ||
            fetchedData.item,
          amountPaid:
            fetchedData["Amount Paid"] ||
            fetchedData.amountPaid ||
            fetchedData.amount,
          price: fetchedData["Price"] || fetchedData.price,
          tax: fetchedData["Tax"] || fetchedData.tax,
        };

        processedData = [normalizedItem];
      }

      const fetchTime = new Date();
      const userName =
        user?.name || user?.email?.split("@")[0] || "Unknown User";

      // Cache the data globally
      localStorage.setItem(
        "hbc_transactions_cache",
        JSON.stringify(processedData)
      );
      localStorage.setItem("hbc_transactions_cache_date", today);
      localStorage.setItem(
        "hbc_transactions_cache_time",
        fetchTime.toISOString()
      );
      localStorage.setItem("hbc_transactions_cache_user", userName);

      setData(processedData);
      setLastFetched(fetchTime);
      setSuccess(`✅ Synced ${processedData.length} transaction records`);

      // Update available columns based on data
      if (processedData.length > 0) {
        // Update visible columns for new fields
        const newVisibleColumns = { ...visibleColumns };
        Object.keys(processedData[0]).forEach((key) => {
          if (!(key in newVisibleColumns)) {
            newVisibleColumns[key] = true;
          }
        });
        setVisibleColumns(newVisibleColumns);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setError(`❌ Failed to fetch transactions: ${error.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if data was fetched today by anyone
  const isDataFetchedToday = () => {
    const today = new Date().toDateString();
    const cacheDate = localStorage.getItem("hbc_transactions_cache_date");
    return cacheDate === today;
  };

  // Get cached user info
  const getCachedUserInfo = () => {
    const cacheTime = localStorage.getItem("hbc_transactions_cache_time");
    const cacheUser = localStorage.getItem("hbc_transactions_cache_user");

    if (cacheTime && cacheUser) {
      const date = new Date(cacheTime);
      return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString(),
        user: cacheUser,
      };
    }
    return null;
  };

  // Filter and sort data when dependencies change
  useEffect(() => {
    const timeFiltered = filterDataByTimePeriod(data);
    const paymentTypeFiltered = filterDataByPaymentType(timeFiltered);
    const sorted = sortData(paymentTypeFiltered);
    setFilteredData(sorted);
  }, [data, selectedFilter, selectedPaymentType, customDateRange, sortConfig]);

  // Auto-fetch on component mount
  useEffect(() => {
    if (canAccessMindbody()) {
      fetchTransactions();
    }
  }, []);

  // Access control
  if (!canAccessMindbody()) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            Access Denied: Guests cannot access HBC Charges.
          </div>
        </div>
      </div>
    );
  }

  const customizableColumns = ["description", "price", "tax"]; // Only these can be hidden (Date and Amount Paid are required)

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            HBC Charges
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {(() => {
              const cachedInfo = getCachedUserInfo();
              if (cachedInfo) {
                return `Last synced: ${cachedInfo.date} at ${cachedInfo.time} by ${cachedInfo.user}`;
              } else if (lastFetched) {
                const userName =
                  user?.name || user?.email?.split("@")[0] || "Unknown User";
                return `Last synced: ${lastFetched.toLocaleDateString()} at ${lastFetched.toLocaleTimeString()} by ${userName}`;
              }
              return "No data synced yet";
            })()}
          </p>
        </div>
        <button
          onClick={fetchTransactions}
          disabled={loading || isDataFetchedToday()}
          className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
        >
          {loading
            ? "Syncing..."
            : isDataFetchedToday()
            ? "Already Synced Today"
            : "Fetch Transactions"}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button
            onClick={() => setSuccess("")}
            className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      )}

      {/* Transaction Data Results */}
      {filteredData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {getTransactionHistoryTitle()}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {getFilterTitle()}
                </p>
              </div>

              <div className="flex items-center space-x-3">
                {/* Export Options - Icon Only */}
                <button
                  onClick={exportToCSV}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Export CSV"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </button>

                <button
                  onClick={handlePrint}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Print"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                  </svg>
                </button>

                {/* Payment Type Filter */}
                <div className="relative">
                  <select
                    value={selectedPaymentType}
                    onChange={(e) => setSelectedPaymentType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="all">All Records</option>
                    <option value="hbc">HBC Charges</option>
                    <option value="ghmbl">GHMBL Payments</option>
                  </select>
                </div>

                {/* Time Period Filter */}
                <div className="relative">
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="thisWeek">This Week</option>
                    <option value="lastWeek">Last Week</option>
                    <option value="last6Months">Last 6 Months</option>
                    <option value="thisYear">This Year</option>
                    <option value="lastYear">Last Year</option>
                    <option value="custom">Custom Range</option>
                    <option value="all">All Time</option>
                  </select>
                </div>

                {/* Customize Columns Button */}
                <button
                  onClick={() => setShowCustomizeColumns(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Customize Columns"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                  </svg>
                </button>
              </div>
            </div>

            {/* Custom Date Range */}
            {selectedFilter === "custom" && (
              <div className="flex items-center space-x-3 mt-4">
                <input
                  type="date"
                  value={customDateRange.startDate}
                  onChange={(e) =>
                    setCustomDateRange((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <span className="text-gray-600 dark:text-gray-400">to</span>
                <input
                  type="date"
                  value={customDateRange.endDate}
                  onChange={(e) =>
                    setCustomDateRange((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  {/* Date - Always visible */}
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("saleDate")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date</span>
                      <span className="text-gray-400">
                        {getSortIcon("saleDate")}
                      </span>
                    </div>
                  </th>

                  {/* Description */}
                  {visibleColumns.description && (
                    <th
                      className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      onClick={() => handleSort("description")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Description</span>
                        <span className="text-gray-400">
                          {getSortIcon("description")}
                        </span>
                      </div>
                    </th>
                  )}

                  {/* Amount Charged - Always visible */}
                  <th
                    className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort("amountPaid")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Amount Charged</span>
                      <span className="text-gray-400">
                        {getSortIcon("amountPaid")}
                      </span>
                    </div>
                  </th>

                  {/* Other columns */}
                  {Object.keys(filteredData[0] || {}).map((key) => {
                    if (
                      key === "saleDate" ||
                      key === "description" ||
                      key === "amountPaid"
                    )
                      return null;
                    if (!visibleColumns[key]) return null;

                    return (
                      <th
                        key={key}
                        className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handleSort(key)}
                      >
                        <div className="flex items-center space-x-1">
                          <span>
                            {key.charAt(0).toUpperCase() +
                              key.slice(1).replace(/([A-Z])/g, " $1")}
                          </span>
                          <span className="text-gray-400">
                            {getSortIcon(key)}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredData.map((record, index) => (
                  <tr
                    key={index}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      shouldHighlightRow(record.description)
                        ? "bg-yellow-200 dark:bg-yellow-800/30 border-l-4 border-yellow-600"
                        : ""
                    }`}
                  >
                    {/* Date */}
                    <td className="py-4 px-6">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {formatDate(record.saleDate)}
                      </p>
                    </td>

                    {/* Description */}
                    {visibleColumns.description && (
                      <td className="py-4 px-6">
                        <p
                          className={`text-gray-900 dark:text-white ${
                            shouldHighlightRow(record.description)
                              ? "font-semibold"
                              : ""
                          }`}
                        >
                          {formatDescription(record.description)}
                        </p>
                      </td>
                    )}

                    {/* Amount Charged */}
                    <td className="py-4 px-6">
                      <p
                        className={`font-semibold ${
                          shouldHighlightRow(record.description)
                            ? "text-red-600 dark:text-red-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        {formatCurrency(record.amountPaid, record.description)}
                      </p>
                    </td>

                    {/* Other columns */}
                    {Object.entries(record).map(([key, value]) => {
                      if (
                        key === "saleDate" ||
                        key === "description" ||
                        key === "amountPaid"
                      )
                        return null;
                      if (!visibleColumns[key]) return null;

                      const isCurrency =
                        key.toLowerCase().includes("price") ||
                        key.toLowerCase().includes("tax") ||
                        key.toLowerCase().includes("amount");

                      return (
                        <td key={key} className="py-4 px-6">
                          <p
                            className={`text-gray-900 dark:text-white ${
                              isCurrency
                                ? shouldHighlightRow(record.description)
                                  ? "font-semibold text-red-600 dark:text-red-400"
                                  : "font-semibold text-green-600 dark:text-green-400"
                                : ""
                            }`}
                          >
                            {isCurrency
                              ? formatCurrency(value, record.description)
                              : String(value || "N/A")}
                          </p>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {filteredData.length === 0 && !loading && data.length === 0 && (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">🛒</span>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Transaction Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Click "Fetch Transactions" to retrieve the latest data from Google
            Scripts
          </p>
          <button
            onClick={fetchTransactions}
            disabled={loading || isDataFetchedToday()}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
          >
            {loading
              ? "Syncing..."
              : isDataFetchedToday()
              ? "Already Synced Today"
              : "Fetch Transactions Now"}
          </button>
        </div>
      )}

      {/* No Results for Filter */}
      {filteredData.length === 0 && !loading && data.length > 0 && (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">📅</span>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Transactions Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {selectedPaymentType === "ghmbl"
              ? "No GHMBL Payments found"
              : selectedPaymentType === "hbc"
              ? "No HBC Charges found"
              : "No transactions found"}{" "}
            for {getFilterTitle()}
          </p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => setSelectedPaymentType("all")}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition duration-200"
            >
              Show All Records
            </button>
            <button
              onClick={() => setSelectedFilter("all")}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition duration-200"
            >
              Show All Time
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Fetching transaction data...
          </p>
        </div>
      )}

      {/* Customize Columns Side Panel */}
      {showCustomizeColumns && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
          <div className="bg-white dark:bg-gray-800 h-full w-80 shadow-xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Customize Columns
                </h3>
                <button
                  onClick={() => setShowCustomizeColumns(false)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select which columns to show. Date and Amount Charged are always
                visible.
              </p>

              <div className="space-y-3">
                {/* Required columns - disabled */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Date
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Required
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Amount Charged
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Required
                  </span>
                </div>

                {/* Customizable columns */}
                {customizableColumns.map((columnKey) => (
                  <div
                    key={columnKey}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {columnKey.charAt(0).toUpperCase() +
                        columnKey.slice(1).replace(/([A-Z])/g, " $1")}
                    </span>
                    <button
                      onClick={() => toggleColumnVisibility(columnKey)}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        visibleColumns[columnKey]
                          ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200"
                          : "bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                      }`}
                    >
                      {visibleColumns[columnKey] ? "Visible" : "Hidden"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentScreen, setCurrentScreen] = useState("dashboard");
  const [selectedPlayerId, setSelectedPlayerId] = useState(null); // For individual player view
  const [showAddSession, setShowAddSession] = useState(false);
  const [isSelectingForCancel, setIsSelectingForCancel] = useState(false);
  const [showPayNow, setShowPayNow] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setLoading(false);

      if (user) {
        try {
          const lastUpdated = localStorage.getItem(`ghmbl-last-signin-update-${user.uid}`);
          const today = new Date().toDateString();
          if (lastUpdated !== today) {
            const playerRef = doc(db, "players", user.uid);
            await updateDoc(playerRef, {
              lastSignIn: new Date().toISOString()
            });
            localStorage.setItem(`ghmbl-last-signin-update-${user.uid}`, today);
          }
        } catch (error) {
          console.error("Error updating lastSignIn metadata:", error);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = () => {
    // User will be set automatically by onAuthStateChanged
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setCurrentScreen("dashboard");
      setSelectedPlayerId(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const fetchDashboardData = () => {
    // This function can be used to refresh dashboard data
    // For now, it's a placeholder that can be expanded later
    console.log("Fetching dashboard data...");
  };

  const handlePlayerClick = (playerId) => {
    console.log("handlePlayerClick: Clicked on player with ID:", playerId);
    setSelectedPlayerId(playerId);
    setCurrentScreen("player-detail");
    console.log(
      "handlePlayerClick: Navigation to player-detail screen initiated"
    );
  };

  const fetchPayments = async () => {
    // Fetch payments data - placeholder for now
    console.log("Fetching payments...");
  };

  const fetchCurrentBalance = async () => {
    // Fetch current balance - placeholder for now
    console.log("Fetching current balance...");
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case "dashboard":
        return <DashboardScreen setCurrentScreen={setCurrentScreen} />;
      case "players":
        return <PlayersScreen onPlayerClick={handlePlayerClick} />;
      case "player-detail":
        return (
          <PlayerDetailScreen
            playerId={selectedPlayerId}
            onBack={() => setCurrentScreen("players")}
            setCurrentScreen={setCurrentScreen}
          />
        );
      case "attendance":
        return <AttendanceScreen />;
      case "payments":
        return <PaymentsScreen />;
      case "reports":
        return <ReportsScreen />;
      case "mindbody":
        return <MindbodyScreen />;
      case "account":
        return <AccountScreen />;
      default:
        return <DashboardScreen setCurrentScreen={setCurrentScreen} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-indigo-600 dark:bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">🏸</span>
          </div>
          <p className="text-gray-600 dark:text-gray-300">Loading GHMBL...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <ThemeProvider>
        <AuthContext.Provider value={{ user }}>
          <LoginScreen onLogin={handleLogin} />
        </AuthContext.Provider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthContext.Provider value={{ user }}>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
          <Navigation
            currentScreen={currentScreen}
            setCurrentScreen={setCurrentScreen}
            onLogout={handleLogout}
          />
          <div className="flex-1 overflow-auto">
            {/* Spacer for mobile burger menu */}
            <div className="lg:hidden h-16"></div>

            {/* Render screen based on navigation - all users can navigate */}
            {renderScreen()}
          </div>
        </div>
      </AuthContext.Provider>

      {/* Add/Cancel Session Modal */}
      {showAddSession && (
        <AddSessionModal
          onClose={() => {
            setShowAddSession(false);
            setIsSelectingForCancel(false);
          }}
          onSessionAdded={() => {
            setShowAddSession(false);
            setIsSelectingForCancel(false);
            fetchDashboardData();
          }}
          isSelectingForCancel={isSelectingForCancel}
        />
      )}
    </ThemeProvider>
  );
}

export default App;
